"use strict";
var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.InboundClient = void 0;
const core_auth_1 = require("@deliverr/core-auth");
const buffer_1 = require("buffer");
const commons_logs_1 = require("@deliverr/commons-logs");
const commons_objects_1 = require("@deliverr/commons-objects");
const errors_1 = require("@deliverr/errors");
const client_auth_1 = require("@deliverr/client-auth");
const lodash_1 = require("lodash");
const BoxLabelsFormat_1 = require("./BoxLabelsFormat");
const InboundError_1 = require("./InboundError");
const InboundShipment_1 = require("./InboundShipment");
const ShipmentFileUploadType_1 = require("./Shipment/ShipmentFileUploadType");
const ShippingPlanDispersalMethod_1 = require("./ShippingPlanDispersalMethod");
const DynamicBaseUrlClient_1 = require("./clients/DynamicBaseUrlClient");
const ClientUtils_1 = require("./utils/ClientUtils");
const defaultConfig = {
    authEngine: core_auth_1.AuthEngine.UNKNOWN,
    baseURL: process.env.INBOUND_SERVICE_URL,
};
class InboundClient {
    constructor(config = {}) {
        const logCtx = { fn: "InboundClient#constructor" };
        const clientConfig = Object.assign(Object.assign({}, defaultConfig), config);
        if (clientConfig.authEngine === core_auth_1.AuthEngine.UNKNOWN) {
            throw new Error("authEngine for InboundClient is not set");
        }
        if (clientConfig.authEngine === core_auth_1.AuthEngine.COGNITO_INTERNAL) {
            const tokenHandler = new client_auth_1.OAuthClientTokenBuilder(config);
            clientConfig.tokenRefreshFn = tokenHandler.getTokenFn();
        }
        if (!clientConfig.baseURL) {
            commons_logs_1.log.warn(logCtx, "baseURL is not configured");
        }
        this.dynamicClient = new DynamicBaseUrlClient_1.DynamicBaseUrlClient(clientConfig);
    }
    async getClient(sellerId) {
        return await this.dynamicClient.getApiClient(sellerId);
    }
    async getCrossdockQuote(sellerId, shipmentId) {
        const url = `/v1/shipment/${sellerId}/${shipmentId}/crossdock-quote`;
        try {
            return await (await this.getClient(sellerId)).get(url);
        }
        catch (error) {
            throw await this.getInboundDeliverrError(error, url);
        }
    }
    async getShippingPlan(sellerId, shippingPlanId) {
        if (!sellerId) {
            throw new errors_1.DeliverrError({
                code: InboundError_1.InboundError.UNDEFINED_PARAMETER,
                payload: {
                    sellerId: sellerId || "undefined",
                    shippingPlanId: shippingPlanId || "undefined",
                },
                message: "Missing sellerId",
            });
        }
        const url = `/v1/shipping-plan/${sellerId}/${shippingPlanId}`;
        try {
            return await (await this.getClient(sellerId)).get(url);
        }
        catch (error) {
            throw await this.getInboundDeliverrError(error, url);
        }
    }
    async getShippingPlanInfoByIds(shippingPlanIds) {
        const query = shippingPlanIds.map((planId) => `shippingPlanId=${planId}`).join("&");
        const url = `v1/shipping-plan/query/byIds?${query}`;
        try {
            return await (await this.getClient(ClientUtils_1.DUMMY_SELLER_ID)).get(url);
        }
        catch (error) {
            throw await this.getInboundDeliverrError(error, url);
        }
    }
    async getShippingPlanWithBasicAuth(shippingPlanId) {
        const url = `/v1/shipping-plan/${shippingPlanId}`;
        try {
            return await (await this.getClient(ClientUtils_1.DUMMY_SELLER_ID)).get(url);
        }
        catch (error) {
            throw await this.getInboundDeliverrError(error, url);
        }
    }
    async getShippingPlanIdsByCdskus(cdskus) {
        const url = "/v1/shipping-plans/cdskus";
        try {
            return await (await this.getClient(ClientUtils_1.DUMMY_SELLER_ID)).post(url, {
                cdskus,
            });
        }
        catch (error) {
            commons_logs_1.log.warn({ obj: error, response: error.response }, "raw err");
            throw await this.getInboundDeliverrError(error, url);
        }
    }
    async createEmptyBoxBreakOutputPackages(nPackages, crossDockWarehouseId) {
        const url = `/v1/package/cross-dock/create-empty-packages`;
        try {
            return await (await this.getClient(ClientUtils_1.DUMMY_SELLER_ID)).post(url, {
                nPackages,
                crossDockWarehouseId,
            });
        }
        catch (error) {
            throw await this.getInboundDeliverrError(error, url);
        }
    }
    async breakPackage(cdsku) {
        const url = `/v1/package/cross-dock/${cdsku}/break`;
        try {
            return await (await this.getClient(ClientUtils_1.DUMMY_SELLER_ID)).post(url, {});
        }
        catch (error) {
            throw await this.getInboundDeliverrError(error, url);
        }
    }
    async addItemsToBoxBreakOutputPackage(cdsku, items) {
        const url = `/v1/package/cross-dock/add-items`;
        try {
            return await (await this.getClient(ClientUtils_1.DUMMY_SELLER_ID)).post(url, { cdsku, items });
        }
        catch (error) {
            throw await this.getInboundDeliverrError(error, url);
        }
    }
    async createShippingPlanV2(newShippingPlan) {
        var _a, _b, _c, _d;
        const { sellerId, name, sessionUuid, inboundSource } = newShippingPlan;
        const url = `/v1/shipping-plan/${sellerId}`;
        try {
            return await (await this.getClient(sellerId)).post(url, {
                sellerId,
                name,
                useCasePack: (_a = newShippingPlan.useCasePack) !== null && _a !== void 0 ? _a : false,
                isForwarding: (_b = newShippingPlan.isForwarding) !== null && _b !== void 0 ? _b : false,
                isPromotional: (_c = newShippingPlan.isPromotional) !== null && _c !== void 0 ? _c : false,
                dispersalMethod: (_d = newShippingPlan.dispersalMethod) !== null && _d !== void 0 ? _d : ShippingPlanDispersalMethod_1.ShippingPlanDispersalMethod.DIRECT,
                inboundSource,
                sessionUuid,
                fromAddress: newShippingPlan.fromAddress,
                externalShippingPlanId: newShippingPlan.externalShippingPlanId,
            });
        }
        catch (error) {
            throw await this.getInboundDeliverrError(error, url);
        }
    }
    async createShippingPlan(sellerId, name, useCasePack = false, isForwarding = false, dispersalMethod = ShippingPlanDispersalMethod_1.ShippingPlanDispersalMethod.DIRECT, isPromotional = false) {
        const url = `/v1/shipping-plan/${sellerId}`;
        try {
            return await (await this.getClient(sellerId)).post(url, {
                sellerId,
                name,
                useCasePack,
                isForwarding,
                isPromotional,
                dispersalMethod,
            });
        }
        catch (error) {
            throw await this.getInboundDeliverrError(error, url);
        }
    }
    async updateMappedPackages(newToOriginalCdskuMapping) {
        const url = `/v1/package/cdskus/mapped`;
        try {
            return await (await this.getClient(ClientUtils_1.DUMMY_SELLER_ID)).post(url, newToOriginalCdskuMapping);
        }
        catch (error) {
            throw await this.getInboundDeliverrError(error, url);
        }
    }
    async deactivateSellerCreatedPackages(shippingPlanId) {
        const url = `/v1/package/${shippingPlanId}/deactivate-seller-created-packages`;
        try {
            return await (await this.getClient(ClientUtils_1.DUMMY_SELLER_ID)).post(url, {});
        }
        catch (error) {
            throw await this.getInboundDeliverrError(error, url);
        }
    }
    async bulkModifyPackageIsActive(packageActivationRequest) {
        const url = `/v1/package/activate/bulk`;
        try {
            return await (await this.getClient(ClientUtils_1.DUMMY_SELLER_ID)).post(url, packageActivationRequest);
        }
        catch (error) {
            throw await this.getInboundDeliverrError(error, url);
        }
    }
    async deactivatePackageByCdsku(cdsku) {
        const url = `/v1/package/${cdsku}`;
        try {
            return await (await this.getClient(ClientUtils_1.DUMMY_SELLER_ID)).delete(url, {});
        }
        catch (error) {
            throw await this.getInboundDeliverrError(error, url);
        }
    }
    async deactivatePackageByCdskuV2(sellerId, shippingPlanId, cdsku) {
        const url = `/v2/package/${sellerId}/${shippingPlanId}/deactivate/${cdsku}`;
        try {
            return await (await this.getClient(sellerId)).delete(url, {
                sellerId,
                shippingPlanId,
                cdsku,
            });
        }
        catch (error) {
            throw await this.getInboundDeliverrError(error, url);
        }
    }
    async getOpenShipments(sellerId, dsku) {
        const url = `/v1/shipment/${sellerId}/open/${dsku}`;
        try {
            return await (await this.getClient(sellerId)).get(url);
        }
        catch (error) {
            throw await this.getInboundDeliverrError(error, url);
        }
    }
    async getInboundQties(sellerId, dskus) {
        const url = `/v1/shipment/${sellerId}/inbound`;
        try {
            return await (await this.getClient(sellerId)).get(url, { dskus });
        }
        catch (error) {
            throw await this.getInboundDeliverrError(error, url);
        }
    }
    async setShippingPlanItems(sellerId, planId, items) {
        const url = `/v1/shipping-plan/${sellerId}/${planId}/item`;
        try {
            return await (await this.getClient(sellerId)).post(url, {
                items,
            });
        }
        catch (error) {
            throw await this.getInboundDeliverrError(error, url);
        }
    }
    async getShippingPlanItems(sellerId, shippingPlanId) {
        const url = `/v1/shipping-plan/${sellerId}/${shippingPlanId}/items`;
        try {
            return await (await this.getClient(sellerId)).get(url);
        }
        catch (error) {
            throw await this.getInboundDeliverrError(error, url);
        }
    }
    async getShippingPlanMilestone(sellerId, shippingPlanId, lastMilestoneTimestamp) {
        const url = `/v1/shipping-plan/${sellerId}/${shippingPlanId}/milestones`;
        try {
            return await (await this.getClient(sellerId)).get(url, {
                lastMilestoneTimestamp,
            });
        }
        catch (error) {
            throw await this.getInboundDeliverrError(error, url);
        }
    }
    async getPackagesByCdskus(cdskus) {
        const url = `/v1/package/cross-dock`;
        try {
            return await (await this.getClient(ClientUtils_1.DUMMY_SELLER_ID)).post(url, {
                cdskus,
            });
        }
        catch (error) {
            throw await this.getInboundDeliverrError(error, url);
        }
    }
    async getPackagesByShipmentId({ shipmentId, limit, offset, includeInactive, }) {
        const url = `/v1/package/byShipment/${shipmentId}`;
        const requestBody = { limit, offset, includeInactive };
        try {
            return (await this.getClient(ClientUtils_1.DUMMY_SELLER_ID)).post(url, requestBody);
        }
        catch (error) {
            throw await this.getInboundDeliverrError(error, url);
        }
    }
    async getCdskusProductSummary(cdskus) {
        const url = "/v1/package/product-summary";
        try {
            return (await this.getClient(ClientUtils_1.DUMMY_SELLER_ID)).axiosInstance
                .request({
                url,
                data: {
                    cdskus,
                },
            })
                .then((res) => res.data);
        }
        catch (error) {
            throw await this.getInboundDeliverrError(error, url);
        }
    }
    async getPackageByCdskuAfterBreak(cdsku) {
        const url = `/v1/package/cross-dock/after-break`;
        try {
            return await (await this.getClient(ClientUtils_1.DUMMY_SELLER_ID)).get(url, { cdsku });
        }
        catch (error) {
            throw await this.getInboundDeliverrError(error, url);
        }
    }
    async getCrossdockPackagesByDateRange(sellerId, startDate, endDate) {
        const url = `/v1/package/cross-dock/${sellerId}/startDate/${startDate.toISOString()}/endDate/${endDate.toISOString()}`;
        try {
            return await (await this.getClient(ClientUtils_1.EBS_SELLER_ID)).get(url);
        }
        catch (error) {
            throw await this.getInboundDeliverrError(error, url);
        }
    }
    async generateShipments(sellerId, planId, dskuToBarcode, shipOriginZip) {
        const url = `/v1/shipment/${sellerId}/generate-shipments`;
        try {
            return await (await this.getClient(sellerId)).post(url, {
                shippingPlanId: planId,
                dskuToBarcode,
                shipOriginZip,
            });
        }
        catch (error) {
            throw await this.getInboundDeliverrError(error, url);
        }
    }
    async generateShipmentsV2(sellerId, planId) {
        const url = `/v1/shipment/${sellerId}/generate-shipments`;
        try {
            return await (await this.getClient(sellerId)).post(url, {
                shippingPlanId: planId,
            });
        }
        catch (error) {
            throw await this.getInboundDeliverrError(error, url);
        }
    }
    async generateShipmentsWithRequestId(requestId, sellerId, planId) {
        const url = `/v2/shipment/${requestId}/generate-shipments`;
        try {
            return await (await this.getClient(sellerId)).post(url, {
                sellerId,
                shippingPlanId: planId,
            });
        }
        catch (error) {
            throw await this.getInboundDeliverrError(error, url);
        }
    }
    async allocateInventory(sellerId, planId) {
        const url = `/v1/shipment/${sellerId}/allocate-inventory`;
        try {
            return await (await this.getClient(sellerId)).post(url, {
                shippingPlanId: planId,
            });
        }
        catch (error) {
            throw await this.getInboundDeliverrError(error, url);
        }
    }
    async createShipment(shippingPlanId, warehouseId, asnId, shippingOption) {
        const url = "/v1/shipment/create-shipment";
        try {
            return await (await this.getClient(ClientUtils_1.DUMMY_SELLER_ID)).post(url, {
                shippingPlanId,
                warehouseId,
                asnId,
                shippingOption,
            });
        }
        catch (error) {
            throw await this.getInboundDeliverrError(error, url);
        }
    }
    async getShipments(sellerId, planId, shouldFetchDataDocksLink) {
        const url = `/v1/shipment/${sellerId}`;
        try {
            return await (await this.getClient(sellerId)).get(url, Object.assign({ shippingPlanId: planId }, (shouldFetchDataDocksLink !== undefined && { shouldFetchDataDocksLink })));
        }
        catch (error) {
            throw await this.getInboundDeliverrError(error, url);
        }
    }
    async getShipment(sellerId, shipmentId) {
        const url = `/v1/shipment/${sellerId}/${shipmentId}`;
        try {
            return await (await this.getClient(sellerId)).get(url);
        }
        catch (error) {
            throw await this.getInboundDeliverrError(error, url);
        }
    }
    async getShipmentFromAsnId(asnId) {
        const url = `/v1/shipment/asn/${asnId}`;
        try {
            return await (await this.getClient(ClientUtils_1.DUMMY_SELLER_ID)).get(url);
        }
        catch (error) {
            throw await this.getInboundDeliverrError(error, url);
        }
    }
    async getShipmentWithBasicAuth(shipmentId) {
        const url = `/v1/shipment-internal/${shipmentId}`;
        try {
            return await (await this.getClient(ClientUtils_1.DUMMY_SELLER_ID)).get(url);
        }
        catch (error) {
            throw await this.getInboundDeliverrError(error, url);
        }
    }
    async getShipmentWeight(sellerId, shipmentId) {
        const url = `/v1/shipment/${sellerId}/${shipmentId}/weight`;
        try {
            return await (await this.getClient(sellerId)).get(url);
        }
        catch (error) {
            throw await this.getInboundDeliverrError(error, url);
        }
    }
    async updateTrackingForPackage(packageId, sellerId, trackingNumber) {
        const url = `/v1/package/${sellerId}/${packageId}/tracking`;
        try {
            return await (await this.getClient(sellerId)).post(url, {
                trackingNumber,
            });
        }
        catch (error) {
            throw await this.getInboundDeliverrError(error, url);
        }
    }
    async updateTrackingForAllPackages(shipmentId, sellerId, trackingNumber) {
        const url = `/v1/package/${sellerId}/tracking`;
        try {
            return await (await this.getClient(sellerId)).post(url, {
                shipmentId,
                trackingNumber,
            });
        }
        catch (error) {
            throw await this.getInboundDeliverrError(error, url);
        }
    }
    async setPackagesV2(sellerId, shipmentPkgConfig, shouldSaveAsDraft) {
        const url = `/v2/package/${sellerId}${shouldSaveAsDraft ? `/?shouldSaveAsDraft=${shouldSaveAsDraft}` : ""}`;
        try {
            return await (await this.getClient(sellerId)).post(url, shipmentPkgConfig);
        }
        catch (error) {
            throw await this.getInboundDeliverrError(error, url);
        }
    }
    async setPackagesDimsAndWeights(shipmentPkgConfig, shouldSaveAsDraft) {
        const url = `/v2/package/dims-and-weights${shouldSaveAsDraft ? `/?shouldSaveAsDraft=${shouldSaveAsDraft}` : ""}`;
        try {
            return await (await this.getClient(ClientUtils_1.DUMMY_SELLER_ID)).put(url, shipmentPkgConfig);
        }
        catch (error) {
            throw await this.getInboundDeliverrError(error, url);
        }
    }
    async getActivePackages(sellerId, shipmentId) {
        const url = `/v1/package/${sellerId}`;
        try {
            return await (await this.getClient(ClientUtils_1.EBS_SELLER_ID)).get(url, { shipmentId });
        }
        catch (error) {
            throw await this.getInboundDeliverrError(error, url);
        }
    }
    async getActivePackagesByShippingPlan(sellerId, shippingPlanId, limit, offset) {
        const url = `/v1/package/${sellerId}/byShippingPlan/${shippingPlanId}`;
        try {
            return await (await this.getClient(ClientUtils_1.EBS_SELLER_ID)).getCached(url, { limit, offset });
        }
        catch (error) {
            throw await this.getInboundDeliverrError(error, url);
        }
    }
    async getActivePackagesByShippingPlanV2(shippingPlanId, limit, offset) {
        const url = `/v2/package/byShippingPlan/${shippingPlanId}`;
        try {
            return await (await this.getClient(ClientUtils_1.EBS_SELLER_ID)).getCached(url, { limit, offset });
        }
        catch (error) {
            throw await this.getInboundDeliverrError(error, url);
        }
    }
    async getPackage(sellerId, packageId) {
        const url = `/v1/package/${sellerId}/${packageId}`;
        try {
            return await (await this.getClient(sellerId)).get(url);
        }
        catch (error) {
            throw await this.getInboundDeliverrError(error, url);
        }
    }
    async generateAsn(sellerId, shipmentId) {
        const url = `/v1/asn/${sellerId}`;
        try {
            return await (await this.getClient(sellerId)).post(url, {
                shipmentId: String(shipmentId),
            });
        }
        catch (error) {
            throw await this.getInboundDeliverrError(error, url);
        }
    }
    async completeShipment(sellerId, shipmentId) {
        const url = `/v1/shipment/${sellerId}/${shipmentId}`;
        try {
            return await (await this.getClient(sellerId)).put(url, {
                shipmentId: String(shipmentId),
            });
        }
        catch (error) {
            throw await this.getInboundDeliverrError(error, url);
        }
    }
    async setShipmentArrived(sellerId, shipmentId, timestamp) {
        const url = `/v1/shipment/${sellerId}/${shipmentId}/arrived`;
        try {
            return await (await this.getClient(sellerId)).put(url, {
                timestamp: timestamp.toISOString(),
            });
        }
        catch (error) {
            throw await this.getInboundDeliverrError(error, url);
        }
    }
    async getAsnsByCdsku(cdsku) {
        const url = `/v1/asn`;
        try {
            return (await this.getClient(ClientUtils_1.DUMMY_SELLER_ID)).get(url, { cdsku });
        }
        catch (error) {
            throw await this.getInboundDeliverrError(error, url);
        }
    }
    async getAsnsByShipmentId(sellerId, shipmentId) {
        const url = `/v1/asn/${sellerId}`;
        try {
            return await (await this.getClient(sellerId)).get(url, {
                shipmentId: String(shipmentId),
            });
        }
        catch (error) {
            throw await this.getInboundDeliverrError(error, url);
        }
    }
    async getAsnsWithShipmentId(shipmentId) {
        const url = `/v1/asn/query/byShipment/${shipmentId}`;
        try {
            return await (await this.getClient(ClientUtils_1.DUMMY_SELLER_ID)).get(url);
        }
        catch (error) {
            throw await this.getInboundDeliverrError(error, url);
        }
    }
    async getAsnByLabelAndWarehouseId(label, warehouseId) {
        const url = `/v1/asn/query/byLabelAndWarehouseId/${label}/${warehouseId}`;
        try {
            return await (await this.getClient(ClientUtils_1.DUMMY_SELLER_ID)).get(url);
        }
        catch (error) {
            throw await this.getInboundDeliverrError(error, url);
        }
    }
    async duplicateShippingPlan(sellerId, shippingPlanId, name) {
        const url = `/v1/shipping-plan/${sellerId}/${shippingPlanId}/duplicate`;
        try {
            return await (await this.getClient(sellerId)).post(url, { name });
        }
        catch (error) {
            throw await this.getInboundDeliverrError(error, url);
        }
    }
    async undraftCrossdockShipments(shipmentsIds) {
        const url = "/v1/shipment/crossdock/undraft-package-received";
        try {
            return await (await this.getClient(ClientUtils_1.DUMMY_SELLER_ID)).post(url, {
                shipmentsIds,
            });
        }
        catch (error) {
            throw await this.getInboundDeliverrError(error, url);
        }
    }
    async archiveShippingPlan(sellerId, shippingPlanId) {
        const url = `/v1/shipping-plan/${sellerId}/${shippingPlanId}`;
        try {
            return await (await this.getClient(sellerId)).delete(url);
        }
        catch (error) {
            throw await this.getInboundDeliverrError(error, url);
        }
    }
    async archiveSellerShippingPlansWithStatus(sellerId, status) {
        const url = `/v1/shipping-plan/${sellerId}`;
        try {
            return await (await this.getClient(sellerId)).delete(url, { status });
        }
        catch (error) {
            throw await this.getInboundDeliverrError(error, url);
        }
    }
    async renameShippingPlan(sellerId, shippingPlanId, name) {
        const url = `/v1/shipping-plan/${sellerId}/${shippingPlanId}`;
        try {
            return await (await this.getClient(sellerId)).post(url, {
                name,
            });
        }
        catch (error) {
            throw await this.getInboundDeliverrError(error, url);
        }
    }
    async getCasePackDefaults(dskus) {
        const url = `/v1/case-pack/default`;
        try {
            return await (await this.getClient(ClientUtils_1.DUMMY_SELLER_ID)).get(url, {
                dskus,
            });
        }
        catch (error) {
            throw await this.getInboundDeliverrError(error, url);
        }
    }
    async downloadPackingSlip(sellerId, shippingPlanId) {
        const url = `/v1/shipping-plan/${sellerId}/${shippingPlanId}/packing-slip`;
        try {
            const { data } = await (await this.getClient(sellerId)).request({
                method: "GET",
                url,
                responseType: "blob",
            });
            return data;
        }
        catch (error) {
            throw await this.getInboundDeliverrError(error, url);
        }
    }
    async downloadPackingSlipV2(sellerId, shippingPlanId) {
        const url = `/v2/shipping-plan/${sellerId}/${shippingPlanId}/packing-slip`;
        try {
            const { data } = await (await this.getClient(sellerId)).request({
                method: "GET",
                url,
                responseType: "blob",
            });
            return data;
        }
        catch (error) {
            throw await this.getInboundDeliverrError(error, url);
        }
    }
    async downloadBarcodesPdf(layoutType, items, skipValidation) {
        const barcodeOptionsQuery = "includetext=true&scale=2";
        const url = `/v1/barcode/pdf?${barcodeOptionsQuery}`;
        try {
            const requestData = { items, layoutType };
            if (skipValidation !== undefined) {
                requestData.skipValidation = skipValidation;
            }
            const response = await (await this.getClient(ClientUtils_1.DUMMY_SELLER_ID)).request({
                data: requestData,
                method: "POST",
                responseType: "blob",
                url,
            });
            return response.data;
        }
        catch (error) {
            throw await this.getInboundDeliverrError(error, url);
        }
    }
    async generateBoxLabelsAsync(sellerId, shippingPlanId, shipmentId) {
        const url = `/v1/package/${sellerId}/labels/initiate`;
        try {
            return await (await this.getClient(sellerId)).post(url, undefined, {
                shippingPlanId,
                shipmentId,
            });
        }
        catch (error) {
            throw await this.getInboundDeliverrError(error, url);
        }
    }
    async getBoxLabelsUrl(sellerId, shippingPlanId, shipmentId) {
        const url = `/v1/package/${sellerId}/labels`;
        try {
            return await (await this.getClient(sellerId)).get(url, { shippingPlanId, shipmentId });
        }
        catch (error) {
            throw await this.getInboundDeliverrError(error, url);
        }
    }
    async generateBoxLabelsByFormatAsync(labelFormat, language, sellerId, shippingPlanId, shipmentId) {
        const url = `/v1/package/${sellerId}/labels/initiate`;
        try {
            return await (await this.getClient(sellerId)).post(url, undefined, {
                shippingPlanId,
                shipmentId,
                labelFormat,
                language,
            });
        }
        catch (error) {
            throw await this.getInboundDeliverrError(error, url);
        }
    }
    async getBoxLabelsUrlByFormat(labelFormat, language, sellerId, shippingPlanId, shipmentId) {
        const url = `/v1/package/${sellerId}/labels`;
        try {
            return await (await this.getClient(sellerId)).get(url, {
                shippingPlanId,
                shipmentId,
                labelFormat,
                language,
            });
        }
        catch (error) {
            throw await this.getInboundDeliverrError(error, url);
        }
    }
    async getAllBoxLabelsUrls(sellerId, shippingPlanId, shipmentId) {
        const url = `/v1/package/${sellerId}/labels/all`;
        try {
            return await (await this.getClient(sellerId)).get(url, {
                shippingPlanId,
                shipmentId,
            });
        }
        catch (error) {
            throw await this.getInboundDeliverrError(error, url);
        }
    }
    async updateBoxAuditedPackageItems(packageId, items) {
        const url = "/v1/package/box-audit/update";
        try {
            return await (await this.getClient(ClientUtils_1.DUMMY_SELLER_ID)).post(url, {
                packageId,
                items,
            });
        }
        catch (error) {
            throw await this.getInboundDeliverrError(error, url);
        }
    }
    async ejectBoxAudit(packageId) {
        const url = "/v1/package/box-audit/eject";
        try {
            return await (await this.getClient(ClientUtils_1.DUMMY_SELLER_ID)).post(url, {
                packageId,
            });
        }
        catch (error) {
            throw await this.getInboundDeliverrError(error, url);
        }
    }
    async getMostFrequentlyUsedAddresses(sellerId, addressType) {
        const url = `/v1/shipping-plan/${sellerId}/address`;
        try {
            return await (await this.getClient(sellerId)).get(url, { addressType });
        }
        catch (error) {
            if (error.response.status === 404) {
                return [];
            }
            throw await this.getInboundDeliverrError(error, url);
        }
    }
    async updateShippingPlan(sellerId, shippingPlanId, shippingPlanUpdate) {
        if (!sellerId) {
            throw new errors_1.DeliverrError({
                code: InboundError_1.InboundError.UNDEFINED_PARAMETER,
                payload: {
                    sellerId: sellerId || "undefined",
                    shippingPlanId: shippingPlanId || "undefined",
                    shippingPlanUpdate: shippingPlanUpdate || "undefined",
                },
                message: "Missing sellerId",
            });
        }
        const url = `/v1/shipping-plan/${sellerId}/${shippingPlanId}`;
        try {
            return await (await this.getClient(sellerId)).post(url, shippingPlanUpdate);
        }
        catch (error) {
            throw await this.getInboundDeliverrError(error, url);
        }
    }
    async getAsn(asnId) {
        const url = `/v1/asn/query/byId/${asnId}`;
        try {
            return await (await this.getClient(ClientUtils_1.DUMMY_SELLER_ID)).get(url);
        }
        catch (error) {
            throw await this.getInboundDeliverrError(error, url);
        }
    }
    async getAsnById(sellerId, asnId) {
        const url = `/v1/asn/${sellerId}/${asnId}`;
        try {
            return await (await this.getClient(sellerId)).get(url);
        }
        catch (error) {
            throw await this.getInboundDeliverrError(error, url);
        }
    }
    async getAsnsByShippingPlanId(sellerId, shippingPlanId) {
        const url = `/v1/asn/${sellerId}/${shippingPlanId}/all`;
        try {
            return await (await this.getClient(sellerId)).get(url);
        }
        catch (error) {
            throw await this.getInboundDeliverrError(error, url);
        }
    }
    async getTerminalAsnsByShippingPlanId(sellerId, shippingPlanId) {
        const url = `/v1/asn/${sellerId}/${shippingPlanId}/terminal`;
        try {
            return await (await this.getClient(sellerId)).get(url);
        }
        catch (error) {
            throw await this.getInboundDeliverrError(error, url);
        }
    }
    async getTerminalAsnsWithShippingPlanId(shippingPlanId) {
        const url = `/v1/asn/query/terminal/byShippingPlan/${shippingPlanId}`;
        try {
            return await (await this.getClient(ClientUtils_1.DUMMY_SELLER_ID)).get(url);
        }
        catch (error) {
            throw await this.getInboundDeliverrError(error, url);
        }
    }
    async getActiveAsnForCdsku(cdsku) {
        const url = `/v1/asn/query/byPackage/${cdsku}`;
        try {
            return await (await this.getClient(ClientUtils_1.DUMMY_SELLER_ID)).get(url);
        }
        catch (error) {
            throw await this.getInboundDeliverrError(error, url);
        }
    }
    async getCrossDockAsn(sellerId, shippingPlanId) {
        const url = `/v1/asn/${sellerId}/${shippingPlanId}/crossDock`;
        try {
            return await (await this.getClient(sellerId)).get(url);
        }
        catch (error) {
            throw await this.getInboundDeliverrError(error, url);
        }
    }
    async getCrossDockAsnsByShippingPlanIds(shippingPlanIds) {
        const url = `/v1/asn/query/crossdock/byShippingPlans`;
        try {
            return await (await this.getClient(ClientUtils_1.DUMMY_SELLER_ID)).post(url, {
                shippingPlanIds,
            });
        }
        catch (error) {
            throw await this.getInboundDeliverrError(error, url);
        }
    }
    async generateTransferAsn(cdskus, warehouseId) {
        const url = `/v1/asn/transfer`;
        try {
            return await (await this.getClient(ClientUtils_1.DUMMY_SELLER_ID)).post(url, {
                cdskus,
                warehouseId,
            });
        }
        catch (error) {
            throw await this.getInboundDeliverrError(error, url);
        }
    }
    async rerouteShipment(sellerId, shipmentId, rerouteAsnRequest) {
        const { warehouseId, ignoreCapabilities, ignoreDimsAndWeights, type, notes } = rerouteAsnRequest;
        const url = `/v1/asn/${sellerId}/${shipmentId}/reroute/${warehouseId}`;
        try {
            const query = {};
            if (ignoreDimsAndWeights !== undefined) {
                query.ignoreDimsAndWeights = ignoreDimsAndWeights;
            }
            return await (await this.getClient(sellerId)).post(url, { type, notes, ignoreCapabilities }, query);
        }
        catch (error) {
            throw await this.getInboundDeliverrError(error, url);
        }
    }
    async rerouteCdskus(cdskus, fromWarehouseId, toWarehouseId, type, notes, ignoreDimsAndWeights) {
        const url = `/v1/asn/reroute-cdskus`;
        try {
            return await (await this.getClient(ClientUtils_1.DUMMY_SELLER_ID)).post(url, { cdskus, fromWarehouseId, toWarehouseId, type, notes }, { ignoreDimsAndWeights });
        }
        catch (error) {
            throw await this.getInboundDeliverrError(error, url);
        }
    }
    async getReceivingInfo(sellerId, shipmentId) {
        const url = `/v1/asn/${sellerId}/${shipmentId}/receivingInfo`;
        try {
            return await (await this.getClient(ClientUtils_1.EBS_SELLER_ID)).get(url);
        }
        catch (error) {
            throw await this.getInboundDeliverrError(error, url);
        }
    }
    async getReceivedItems(asnId, warehouseId) {
        const url = `/v1/receiving/asn/query/byAsn/${asnId}/${warehouseId}/receivedItems`;
        try {
            return await (await this.getClient(ClientUtils_1.DUMMY_SELLER_ID)).get(url);
        }
        catch (error) {
            throw await this.getInboundDeliverrError(error, url);
        }
    }
    async getAsnByAsnIdAndWarehouseId(asnId, warehouseId) {
        const url = `/v1/receiving/asn/query/${asnId}/${warehouseId}`;
        try {
            return await (await this.getClient(ClientUtils_1.DUMMY_SELLER_ID)).get(url);
        }
        catch (error) {
            throw await this.getInboundDeliverrError(error, url);
        }
    }
    async getAllRelatedProductsAndCasePacks(asnId, dsku) {
        const url = `/v1/asn/getRelatedProducts`;
        try {
            return await (await this.getClient(ClientUtils_1.DUMMY_SELLER_ID)).post(url, {
                asnId,
                dsku,
            });
        }
        catch (error) {
            throw await this.getInboundDeliverrError(error, url);
        }
    }
    async createNonComplianceAsns(sellerId, shippingPlanId, dsku, expectedQtyMap) {
        const url = `/v1/non-compliance/${sellerId}/${shippingPlanId}/${dsku}/update`;
        try {
            return await (await this.getClient(sellerId)).put(url, { expectedQtyMap });
        }
        catch (error) {
            throw await this.getInboundDeliverrError(error, url);
        }
    }
    async createAsnsForNewDsku(sellerId, shippingPlanId, dsku, source, warehouseId, expectedQty) {
        const url = `/v1/non-compliance/asn/${sellerId}/${shippingPlanId}/${dsku}`;
        try {
            return await (await this.getClient(sellerId)).put(url, {
                warehouseId,
                expectedQty,
                source,
            });
        }
        catch (error) {
            throw await this.getInboundDeliverrError(error, url);
        }
    }
    async createOrUpdateAsnForUnexpectedSkuCase(cdsku, dsku, quantity) {
        const url = `/v1/non-compliance/unexpectedSku/createAsn`;
        try {
            return await (await this.getClient(ClientUtils_1.DUMMY_SELLER_ID)).post(url, {
                cdsku,
                dsku,
                quantity,
            });
        }
        catch (error) {
            throw await this.getInboundDeliverrError(error, url);
        }
    }
    async bulkCreateUnexpectedSkuAsns(unexpectedSkuAsnRequests) {
        const url = `/v1/non-compliance/unexpectedSku/bulkCreateAsn`;
        try {
            return await (await this.getClient(ClientUtils_1.DUMMY_SELLER_ID)).post(url, unexpectedSkuAsnRequests);
        }
        catch (error) {
            throw await this.getInboundDeliverrError(error, url);
        }
    }
    async getTransferAsnsFromCdskus(cdskus) {
        const url = `/v1/transferasn/cdskus`;
        try {
            return (await this.getClient(ClientUtils_1.DUMMY_SELLER_ID)).axiosInstance
                .request({
                url,
                method: "GET",
                data: cdskus,
            })
                .then((res) => res.data);
        }
        catch (error) {
            throw await this.getInboundDeliverrError(error, url);
        }
    }
    async finalizeShipmentUnifiedV2({ shipmentId, crossdockQuoteId }) {
        const url = `/v2/shipment/${shipmentId}/finalization`;
        try {
            await (await this.getClient(ClientUtils_1.DUMMY_SELLER_ID)).post(url, {
                crossdockQuoteId,
            });
        }
        catch (error) {
            const convertedValidationError = this.maybeValidationError(error);
            if (convertedValidationError) {
                throw convertedValidationError;
            }
            throw await this.getInboundDeliverrError(error, url);
        }
    }
    async createRelabelPackages(shipmentId, numPackages, dskuQties) {
        const url = `/v2/relabel-package/${shipmentId}/create`;
        try {
            return await (await this.getClient(ClientUtils_1.DUMMY_SELLER_ID)).post(url, {
                numPackages,
                dskuQties,
            });
        }
        catch (error) {
            throw await this.getInboundDeliverrError(error, url);
        }
    }
    async createPrepPackages(shippingPlanId, numPackages, dskuQties) {
        const url = `/v1/prep-package/${shippingPlanId}`;
        try {
            return await (await this.getClient(ClientUtils_1.DUMMY_SELLER_ID)).post(url, {
                numPackages,
                dskuQties,
            });
        }
        catch (error) {
            throw await this.getInboundDeliverrError(error, url);
        }
    }
    async createReturnsPackage(sellerId, warehouseId, skuQuantities) {
        const url = `/v1/package/returns/create`;
        try {
            return await (await this.getClient(sellerId)).post(url, {
                sellerId,
                warehouseId,
                skuQuantities,
            });
        }
        catch (error) {
            throw await this.getInboundDeliverrError(error, url);
        }
    }
    async scanPrepPackage(sellerId, shippingPlanId, cdsku) {
        const url = `/v1/prep-package/${sellerId}/${shippingPlanId}/scan/${cdsku}`;
        try {
            return await (await this.getClient(sellerId)).put(url, {
                sellerId,
                shippingPlanId,
                cdsku,
            });
        }
        catch (error) {
            throw await this.getInboundDeliverrError(error, url);
        }
    }
    async getPackageByCdsku(cdsku) {
        const url = `/v1/v1/package/byCdsku/${cdsku}`;
        try {
            return await (await this.getClient(ClientUtils_1.DUMMY_SELLER_ID)).get(url, {
                cdsku,
            });
        }
        catch (error) {
            throw await this.getInboundDeliverrError(error, url);
        }
    }
    async getPackageByTrackingNumber(trackingNumber) {
        var _a;
        const url = `/admin/v1/package/get-by-tracking-number/${trackingNumber}`;
        try {
            const { pkg } = await (await this.getClient(ClientUtils_1.DUMMY_SELLER_ID)).get(url);
            return pkg;
        }
        catch (error) {
            if (((_a = error === null || error === void 0 ? void 0 : error.response) === null || _a === void 0 ? void 0 : _a.status) === 404) {
                return undefined;
            }
            throw await this.getInboundDeliverrError(error, url);
        }
    }
    async createCrossdockOutboundPallet(request) {
        const { fromWarehouseId } = request, body = __rest(request, ["fromWarehouseId"]);
        const url = `/v1/pallet/warehouse/${fromWarehouseId}/create-by-pallet-label`;
        try {
            return await (await this.getClient(ClientUtils_1.DUMMY_SELLER_ID)).post(url, body);
        }
        catch (error) {
            throw await this.getInboundDeliverrError(error, url);
        }
    }
    async createPallet(request) {
        const { fromWarehouseId, actionOptions, toWarehouseId, palletBuildStationBarcode } = request;
        const url = `/v1/pallet/warehouse/${fromWarehouseId}`;
        try {
            return await (await this.getClient(ClientUtils_1.DUMMY_SELLER_ID)).post(url, Object.assign({ toWarehouseId, palletBuildStationBarcode }, actionOptions));
        }
        catch (error) {
            throw await this.getInboundDeliverrError(error, url);
        }
    }
    async createPalletFromReceivable(palletLabel, boxLabels, bulkBoxIds, creator, creationReason, units) {
        const url = `/v1/pallet/commit/fromReceivable`;
        return (await this.getClient(ClientUtils_1.DUMMY_SELLER_ID)).post(url, {
            palletLabel,
            boxLabels,
            bulkBoxIds,
            creator,
            creationReason,
            units,
        });
    }
    async reservePalletLabels(numLabels, sequential = false) {
        const url = `/v1/pallet/label/reservation`;
        return (await this.getClient(ClientUtils_1.DUMMY_SELLER_ID)).post(url, {
            numLabels,
            sequential,
        });
    }
    async getPalletItem(cdsku) {
        const url = `/v1/item/${cdsku}`;
        try {
            return await (await this.getClient(ClientUtils_1.DUMMY_SELLER_ID)).get(url);
        }
        catch (error) {
            if (error.response.status === 404) {
                return undefined;
            }
            throw await this.getInboundDeliverrError(error, url);
        }
    }
    async linkPalletById(palletId, cdsku, warehouseId, actionOptions) {
        const url = `/v1/pallet/${palletId}/warehouse/${warehouseId}/item/${cdsku}`;
        try {
            return await (await this.getClient(ClientUtils_1.DUMMY_SELLER_ID)).post(url, Object.assign({}, actionOptions));
        }
        catch (error) {
            throw await this.getInboundDeliverrError(error, url);
        }
    }
    async linkPalletByLabel(labelId, cdsku, warehouseId, actionOptions, ignoreCapabilities) {
        const url = `/v1/pallet/label/${labelId}/warehouse/${warehouseId}/item/${cdsku}`;
        try {
            return await (await this.getClient(ClientUtils_1.DUMMY_SELLER_ID)).post(url, Object.assign(Object.assign({}, actionOptions), { ignoreCapabilities }));
        }
        catch (error) {
            throw await this.getInboundDeliverrError(error, url);
        }
    }
    async getPalletById(palletId) {
        const url = `/v1/pallet/${palletId}`;
        try {
            return await (await this.getClient(ClientUtils_1.DUMMY_SELLER_ID)).get(url);
        }
        catch (error) {
            throw await this.getInboundDeliverrError(error, url);
        }
    }
    async getPalletByLabel(labelId) {
        const url = `/v1/pallet/label/${labelId}`;
        try {
            return await (await this.getClient(ClientUtils_1.DUMMY_SELLER_ID)).get(url);
        }
        catch (error) {
            throw await this.getInboundDeliverrError(error, url);
        }
    }
    async updatePalletTrackingByLabel(labelId, trackingCode, warehouseId, options) {
        const url = `/v1/pallet/label/${labelId}/warehouse/${warehouseId}/tracking/${trackingCode}`;
        try {
            return await (await this.getClient(ClientUtils_1.DUMMY_SELLER_ID)).post(url, Object.assign({}, options));
        }
        catch (error) {
            throw await this.getInboundDeliverrError(error, url);
        }
    }
    async updatePalletMilkRunByLabel(labelId, milkRunId, warehouseId, options) {
        const url = `/v1/pallet/label/${labelId}/warehouse/${warehouseId}/milkRun/${milkRunId}`;
        try {
            return await (await this.getClient(ClientUtils_1.DUMMY_SELLER_ID)).put(url, Object.assign({}, options));
        }
        catch (error) {
            throw await this.getInboundDeliverrError(error, url);
        }
    }
    async putPalletInFc(labelId, user) {
        const url = `/v1/pallet/label/${labelId}/warehouse`;
        try {
            return await (await this.getClient(ClientUtils_1.DUMMY_SELLER_ID)).put(url, user !== null && user !== void 0 ? user : {});
        }
        catch (error) {
            throw await this.getInboundDeliverrError(error, url);
        }
    }
    async getPalletSummary(warehouseId, palletLabelId) {
        const url = `/v1/pallet/summary/${warehouseId}`;
        try {
            return await (await this.getClient(ClientUtils_1.DUMMY_SELLER_ID)).post(url, {
                palletLabelId,
            });
        }
        catch (error) {
            throw await this.getInboundDeliverrError(error, url);
        }
    }
    async getPalletSummaryV2(warehouseId, palletLabel) {
        const url = `/v2/pallet/summary/${warehouseId}`;
        try {
            return await (await this.getClient(ClientUtils_1.DUMMY_SELLER_ID)).post(url, {
                palletLabel,
            });
        }
        catch (error) {
            throw await this.getInboundDeliverrError(error, url);
        }
    }
    async getPallet(palletLabel) {
        const url = `/v1/pallet/query/byLabel/${palletLabel}`;
        try {
            return await (await this.getClient(ClientUtils_1.DUMMY_SELLER_ID)).get(url);
        }
        catch (error) {
            throw await this.getInboundDeliverrError(error, url);
        }
    }
    async palletArrivalScan(warehouseId, palletLabel, user) {
        const url = `/v1/pallet/arrival-scan/${warehouseId}`;
        try {
            return await (await this.getClient(ClientUtils_1.DUMMY_SELLER_ID)).post(url, {
                palletLabel,
                user,
            });
        }
        catch (error) {
            throw await this.getInboundDeliverrError(error, url);
        }
    }
    async validatePalletArrival(warehouseId, labelId, isHub = false, user) {
        const url = `/v1/arrival/${warehouseId}/pallet/label/${labelId}`;
        try {
            return await (await this.getClient(ClientUtils_1.DUMMY_SELLER_ID)).post(url, { isHub, user });
        }
        catch (error) {
            throw await this.getInboundDeliverrError(error, url);
        }
    }
    async generatePalletLabelsZpl(palletId, dpi, warehouseId, options) {
        const url = `/v1/pallet/${palletId}/warehouse/${warehouseId}/labels/zpl`;
        try {
            return await (await this.getClient(ClientUtils_1.DUMMY_SELLER_ID)).post(url, Object.assign({ dpi }, options));
        }
        catch (error) {
            throw await this.getInboundDeliverrError(error, url);
        }
    }
    async generatePalletLabelsPdf(palletId, warehouseId, options) {
        const url = `/v1/pallet/${palletId}/warehouse/${warehouseId}/labels/pdf`;
        try {
            return await (await this.getClient(ClientUtils_1.DUMMY_SELLER_ID)).axiosInstance
                .request({
                method: "post",
                url,
                responseType: "arraybuffer",
                data: Object.assign({}, options),
            })
                .then((response) => new buffer_1.Blob([response.data], { type: "application/pdf" }));
        }
        catch (error) {
            throw await this.getInboundDeliverrError(error, url);
        }
    }
    async closePallet(palletLabelId, warehouseId, actionOptions) {
        const url = `/v1/pallet/label/${palletLabelId}/warehouse/${warehouseId}/close`;
        try {
            return await (await this.getClient(ClientUtils_1.DUMMY_SELLER_ID)).post(url, actionOptions);
        }
        catch (error) {
            throw await this.getInboundDeliverrError(error, url);
        }
    }
    async reopenPallet(palletLabelId, warehouseId, actionOptions) {
        const url = `/v1/pallet/label/${palletLabelId}/warehouse/${warehouseId}/reopen`;
        try {
            return await (await this.getClient(ClientUtils_1.DUMMY_SELLER_ID)).post(url, actionOptions);
        }
        catch (error) {
            throw await this.getInboundDeliverrError(error, url);
        }
    }
    async logPalletAction(palletLabelId, warehouseId, action, actionOptions) {
        const url = `/v1/pallet/label/${palletLabelId}/warehouse/${warehouseId}/action/${action}`;
        try {
            return await (await this.getClient(ClientUtils_1.DUMMY_SELLER_ID)).post(url, actionOptions);
        }
        catch (error) {
            throw await this.getInboundDeliverrError(error, url);
        }
    }
    async markAsnItemsAsNonCompliant(shippingPlanId, dsku) {
        const url = `/v1/non-compliance/${shippingPlanId}/${dsku}/markAsnItemsAsNonCompliant`;
        try {
            return await (await this.getClient(ClientUtils_1.DUMMY_SELLER_ID)).put(url, undefined);
        }
        catch (error) {
            throw await this.getInboundDeliverrError(error, url);
        }
    }
    async getZoneChart(originZip) {
        const url = `/v1/zone-chart/${originZip}`;
        return (await this.getClient(ClientUtils_1.DUMMY_SELLER_ID)).get(url);
    }
    async getNonCompliantSkus(sellerId, shipmentId) {
        const url = `/v1/non-compliance/${sellerId}/${shipmentId}/non-compliant-skus`;
        return (await this.getClient(sellerId)).get(url);
    }
    async addDamagedUnitsToAsnItem(damagdUnitData) {
        const url = `/v1/non-compliance/addDamagedUnitsToAsnItem`;
        try {
            return await (await this.getClient(ClientUtils_1.DUMMY_SELLER_ID)).post(url, damagdUnitData);
        }
        catch (error) {
            throw await this.getInboundDeliverrError(error, url);
        }
    }
    async activateFacilityZone(facilityId, zoneName) {
        const url = "/v1/receiving/facility-zone/zone/activation";
        return (await this.getClient(ClientUtils_1.DUMMY_SELLER_ID)).post(url, {
            facilityId,
            zoneName,
        });
    }
    async deactivateFacilityZones(facilityZoneIds) {
        const url = "/v1/receiving/facility-zone/zone/deactivation";
        return (await this.getClient(ClientUtils_1.DUMMY_SELLER_ID)).post(url, {
            facilityZoneIds,
        });
    }
    async listFacilityZones(facilityId) {
        const url = "/v1/receiving/facility-zone/zone/query/byFacility";
        return (await this.getClient(ClientUtils_1.DUMMY_SELLER_ID)).post(url, {
            facilityId,
        });
    }
    async configureZoneAssignments(sellerIds, facilityZoneId) {
        const url = "/v1/receiving/facility-zone/zone-assignment/configuration";
        return (await this.getClient(ClientUtils_1.DUMMY_SELLER_ID)).post(url, {
            sellerIds,
            facilityZoneId,
        });
    }
    async deactivateZoneAssignments(merchantZoneAssignmentIds) {
        const url = "/v1/receiving/facility-zone/zone-assignment/deactivation";
        return (await this.getClient(ClientUtils_1.DUMMY_SELLER_ID)).post(url, {
            merchantZoneAssignmentIds,
        });
    }
    async listZoneAssignments(facilityId, sellerId) {
        const url = "/v1/receiving/facility-zone/zone-assignment/query";
        return (await this.getClient(sellerId)).post(url, {
            facilityId,
            sellerId,
        });
    }
    async processBoxArrivalScan(label, facilityId) {
        const url = `/v1/box/arrival-scan/${facilityId}`;
        return (await this.getClient(ClientUtils_1.DUMMY_SELLER_ID)).post(url, {
            label,
        });
    }
    async updateBoxArrivalScanEntity(arrivalScanId, entity, facilityId) {
        const url = `/v1/box/arrival-scan/entity/${facilityId}`;
        return (await this.getClient(ClientUtils_1.DUMMY_SELLER_ID)).put(url, {
            arrivalScanId,
            entity,
        });
    }
    async getPackageDispersalMethodByCdsku(cdsku, facilityId) {
        const url = `/v1/box/arrival-scan/dispersal-method/cdsku/${cdsku}/${facilityId}`;
        try {
            return await (await this.getClient(ClientUtils_1.DUMMY_SELLER_ID)).get(url);
        }
        catch (error) {
            throw await this.getInboundDeliverrError(error, url);
        }
    }
    async getPackageDispersalMethodByTrackingNumber(trackingNumber, facilityId) {
        const url = `/v1/box/arrival-scan/dispersal-method/trackingNumber/${trackingNumber}/${facilityId}`;
        try {
            return await (await this.getClient(ClientUtils_1.DUMMY_SELLER_ID)).get(url);
        }
        catch (error) {
            throw await this.getInboundDeliverrError(error, url);
        }
    }
    async receivingAppGetMetadata(warehouseId) {
        const url = `/v1/receiving/metadata/${warehouseId}`;
        return (await this.getClient(ClientUtils_1.DUMMY_SELLER_ID)).get(url);
    }
    async receivingAppValidatePallet(warehouseId, requestBatchId, scannedValue) {
        const url = `/v1/receiving/pallet/validation/bulk/${warehouseId}`;
        return (await this.getClient(ClientUtils_1.DUMMY_SELLER_ID)).post(url, {
            requestBatchId,
            scannedValue,
        });
    }
    async receivingAppValidatePalletBarcode(warehouseId, barcode, requestBatchId, asnId) {
        const url = `/v1/receiving/pallet/validation/sku/${warehouseId}`;
        return (await this.getClient(ClientUtils_1.DUMMY_SELLER_ID)).post(url, {
            barcode,
            requestBatchId,
            asnId,
        });
    }
    async receivingAppValidateBulkPalletBarcode(warehouseId, barcode, requestBatchId, palletLabel) {
        const url = `/v1/receiving/pallet/validation/bulk/sku/${warehouseId}`;
        return (await this.getClient(ClientUtils_1.DUMMY_SELLER_ID)).post(url, {
            barcode,
            requestBatchId,
            palletLabel,
        });
    }
    async receivingAppReceivePalletAndCommit(requestBatchId, dedupKey, warehouseId, asnId, cdsku, dsku, location, lotNumber, numIdenticalBoxes, numUnitsPerBox) {
        const url = `/v1/receiving/pallet/receipt/request/${warehouseId}`;
        return (await this.getClient(ClientUtils_1.DUMMY_SELLER_ID)).post(url, {
            requestBatchId,
            dedupKey,
            asnId,
            cdsku,
            dsku,
            location,
            lotNumber,
            numIdenticalBoxes,
            numUnitsPerBox,
        });
    }
    async palletReceiveAndCommitMultiLocation(requestBatchId, dedupKey, warehouseId, asnLabel, dsku, receivesByLocation, lotNumber, numIdenticalBoxes, numUnitsPerBox) {
        const url = `/v2/receiving/pallet/receipt/request/${warehouseId}`;
        return (await this.getClient(ClientUtils_1.DUMMY_SELLER_ID)).post(url, {
            requestBatchId,
            dedupKey,
            asnLabel,
            dsku,
            receivesByLocation,
            lotNumber,
            numIdenticalBoxes,
            numUnitsPerBox,
        });
    }
    async receivingAppBulkPalletReceiveAndCommit(requestBatchId, dedupKey, warehouseId, palletLabel, dsku, receivesByLocation, numIdenticalBoxes, numUnitsPerBox, lotNumber, expirationDate) {
        const url = `/v1/receiving/pallet/receipt/bulk/request/${warehouseId}`;
        return (await this.getClient(ClientUtils_1.DUMMY_SELLER_ID)).post(url, {
            requestBatchId,
            dedupKey,
            palletLabel,
            dsku,
            receivesByLocation,
            numIdenticalBoxes,
            numUnitsPerBox,
            expirationDate,
            lotNumber,
        });
    }
    async receivingAppValidatePackage(warehouseId, cdsku, requestBatchId) {
        const url = `/v1/receiving/validation/package/${warehouseId}`;
        return (await this.getClient(ClientUtils_1.DUMMY_SELLER_ID)).post(url, {
            cdsku,
            requestBatchId,
        });
    }
    async receivingAppValidateLpn(warehouseId, sellerLPN, requestBatchId) {
        const url = `/v1/receiving/validation/lpn/${warehouseId}`;
        return (await this.getClient(ClientUtils_1.DUMMY_SELLER_ID)).post(url, {
            sellerLPN,
            requestBatchId,
        });
    }
    async receivingAppValidateBoxAdditionToBulkBatch(warehouseId, refCdsku, cdskuToAdd, requestBatchId) {
        const url = `/v1/receiving/validation/bulk/box/${warehouseId}`;
        return (await this.getClient(ClientUtils_1.DUMMY_SELLER_ID)).post(url, {
            refCdsku,
            cdskuToAdd,
            requestBatchId,
        });
    }
    async receivingAppValidateBarcode(warehouseId, cdsku, barcode, requestBatchId) {
        const url = `/v1/receiving/validation/sku/${warehouseId}`;
        return (await this.getClient(ClientUtils_1.DUMMY_SELLER_ID)).post(url, {
            cdsku,
            barcode,
            requestBatchId,
        });
    }
    async receivingAppReceiveUnitsCommit(requestBatchId, dedupKey, warehouseId, cdsku, barcode, location, receivedDelta, expirationDate, lotNumber) {
        const url = `/v3/receiving/receipt/request/${warehouseId}`;
        return (await this.getClient(ClientUtils_1.DUMMY_SELLER_ID)).post(url, {
            requestBatchId,
            dedupKey,
            cdsku,
            barcode,
            location,
            expirationDate,
            receivedDelta,
            lotNumber,
        });
    }
    async boxReceiveAndCommitMultiLocation(requestBatchId, dedupKey, warehouseId, cdsku, dsku, receivesByLocation, images, expirationDate, lotNumber) {
        const url = `/v4/receiving/receipt/request/${warehouseId}`;
        return (await this.getClient(ClientUtils_1.DUMMY_SELLER_ID)).post(url, {
            requestBatchId,
            dedupKey,
            cdsku,
            dsku,
            receivesByLocation,
            images,
            expirationDate,
            lotNumber,
        });
    }
    async lpnReceiveAndCommit(requestBatchId, dedupKey, warehouseId, sellerLPN, dsku, receivesByLocation, images, expirationDate, lotNumber) {
        const url = `/v1/receiving/lpn/request/${warehouseId}`;
        return (await this.getClient(ClientUtils_1.DUMMY_SELLER_ID)).post(url, {
            requestBatchId,
            dedupKey,
            sellerLPN,
            dsku,
            receivesByLocation,
            images,
            expirationDate,
            lotNumber,
        });
    }
    async recordUnexpectedReceiveAttempt({ facilityId, cdsku, dsku, attemptedReceiveQty, }) {
        const url = `/v1/receiving/box/unexpected-receive-attempt`;
        return (await this.getClient(ClientUtils_1.DUMMY_SELLER_ID)).post(url, {
            facilityId,
            cdsku,
            dsku,
            attemptedReceiveQty,
        });
    }
    async recordStorageUnexpectedReceiveAttempt({ facilityId, dsku, asnLabel, attemptedReceiveQty, }) {
        const url = `/v1/receiving/storage/unexpected-receive-attempt`;
        return (await this.getClient(ClientUtils_1.DUMMY_SELLER_ID)).post(url, {
            facilityId,
            dsku,
            asnLabel,
            attemptedReceiveQty,
        });
    }
    async receivingAppReceiveUnitsBulk(requestBatchId, dedupKey, warehouseId, scannedCdskus, dsku, location, numIdenticalBoxes, numUnitsPerBox, expirationDate, lotNumber) {
        const url = `/v1/receiving/receipt/bulk/request/${warehouseId}`;
        return (await this.getClient(ClientUtils_1.DUMMY_SELLER_ID)).post(url, {
            requestBatchId,
            dedupKey,
            scannedCdskus,
            dsku,
            location,
            numIdenticalBoxes,
            numUnitsPerBox,
            expirationDate,
            lotNumber,
        });
    }
    async bulkReceiveAndCommitMultiLocation(requestBatchId, dedupKey, warehouseId, scannedCdskus, dsku, receivesByLocation, numIdenticalBoxes, numUnitsPerBox, expirationDate, lotNumber) {
        const url = `/v2/receiving/receipt/bulk/request/${warehouseId}`;
        return (await this.getClient(ClientUtils_1.DUMMY_SELLER_ID)).post(url, {
            requestBatchId,
            dedupKey,
            scannedCdskus,
            dsku,
            receivesByLocation,
            numIdenticalBoxes,
            numUnitsPerBox,
            expirationDate,
            lotNumber,
        });
    }
    async receivingAppReturnReceiveAndCommit({ facilityId, requestBatchId, dedupKey, dsku, location, receivedDelta, lotNumber, expirationDate, disposition, userId, }) {
        const url = `/v1/receiving/return/receipt/request`;
        return (await this.getClient(ClientUtils_1.DUMMY_SELLER_ID)).post(url, {
            facilityId,
            requestBatchId,
            dedupKey,
            dsku,
            location,
            receivedDelta,
            lotNumber,
            expirationDate,
            disposition,
            userId,
        });
    }
    async getConsolidation(id) {
        const url = `/v1/receiving/consolidation?id=${id}`;
        return (await this.getClient(ClientUtils_1.DUMMY_SELLER_ID)).get(url, { id });
    }
    async consolidationCreateLabel() {
        const url = `/v1/receiving/consolidation/label`;
        return (await this.getClient(ClientUtils_1.DUMMY_SELLER_ID)).post(url, {});
    }
    async createConsolidation(consolidationLabel, fromWarehouseId, toWarehouseId) {
        const url = `/v1/receiving/consolidation/commit`;
        return (await this.getClient(ClientUtils_1.DUMMY_SELLER_ID)).post(url, {
            consolidationLabel,
            fromWarehouseId,
            toWarehouseId,
        });
    }
    async consolidationUpdateStatus(id, status) {
        const url = `/v1/receiving/consolidation/status`;
        return (await this.getClient(ClientUtils_1.DUMMY_SELLER_ID)).post(url, { id, status });
    }
    async consolidationValidateLabel(warehouseId, consolidationLabel) {
        const url = `/v1/receiving/consolidation/validation/label/${warehouseId}`;
        return (await this.getClient(ClientUtils_1.DUMMY_SELLER_ID)).post(url, {
            consolidationLabel,
        });
    }
    async consolidationValidateBarcodeV2({ warehouseId, requestBatchId, consolidationLabel, barcode, }) {
        const url = `/v2/receiving/consolidation/validation/sku/${warehouseId}`;
        return (await this.getClient(ClientUtils_1.DUMMY_SELLER_ID)).post(url, {
            requestBatchId,
            consolidationLabel,
            barcode,
        });
    }
    async consolidationReceiveAndCommit(requestBatchId, dedupKey, consolidationLabel, warehouseId, cdsku, dsku, location, receivedDelta) {
        const url = `/v2/receiving/consolidation/receipt/request/${warehouseId}`;
        return (await this.getClient(ClientUtils_1.DUMMY_SELLER_ID)).post(url, {
            requestBatchId,
            dedupKey,
            consolidationLabel,
            cdsku,
            dsku,
            location,
            receivedDelta,
        });
    }
    async consolidationReceiveAndCommitMultiLocation(requestBatchId, dedupKey, consolidationLabel, warehouseId, dsku, receivesByLocation) {
        const url = `/v3/receiving/consolidation/receipt/request/${warehouseId}`;
        return (await this.getClient(ClientUtils_1.DUMMY_SELLER_ID)).post(url, {
            requestBatchId,
            dedupKey,
            consolidationLabel,
            dsku,
            receivesByLocation,
        });
    }
    async consolidationReceiveAndCommitMultiLocationV2(requestBatchId, dedupKey, consolidationLabel, warehouseId, dsku, receivesByLocation, expirationDate, lotNumber) {
        const url = `/v3/receiving/consolidation/receipt/request/${warehouseId}`;
        return (await this.getClient(ClientUtils_1.DUMMY_SELLER_ID)).post(url, {
            requestBatchId,
            dedupKey,
            consolidationLabel,
            dsku,
            receivesByLocation,
            expirationDate,
            lotNumber,
        });
    }
    async getPackageIdToTransferWarehouse(shippingPlanId) {
        const url = `/v1/package-id-to-transfer-warehouse/${shippingPlanId}`;
        try {
            return await (await this.getClient(ClientUtils_1.DUMMY_SELLER_ID)).get(url);
        }
        catch (error) {
            throw await this.getInboundDeliverrError(error, url);
        }
    }
    async getCostOfCapacitySolutionFromS3() {
        const url = "/v1/warehouse/cost-of-capacity-solution-from-s3";
        try {
            return await (await this.getClient(ClientUtils_1.DUMMY_SELLER_ID)).get(url);
        }
        catch (error) {
            throw await this.getInboundDeliverrError(error, url);
        }
    }
    async getDskuQtyInTransitFromCrossdock(dskus) {
        const url = "/v2/asn/dsku-qty-in-transit-from-crossdock";
        try {
            return await (await this.getClient(ClientUtils_1.DUMMY_SELLER_ID)).post(url, {
                dskus,
            });
        }
        catch (error) {
            throw await this.getInboundDeliverrError(error, url);
        }
    }
    async getActiveShipmentsWithShippingPlanId(shippingPlanId) {
        const url = "/v1/shipment/:sellerId/";
        try {
            return (await this.getClient(ClientUtils_1.DUMMY_SELLER_ID)).get(url, { shippingPlanId });
        }
        catch (error) {
            throw await this.getInboundDeliverrError(error, url);
        }
    }
    async saveUnexpectedCrossdockShippingPlanDskus(shippingPlanId, unexpectedDskus) {
        const url = "/v1/package/cross-dock/unexpected-dskus-in-shipping-plan";
        try {
            return (await this.getClient(ClientUtils_1.DUMMY_SELLER_ID)).post(url, { shippingPlanId, dskus: unexpectedDskus });
        }
        catch (error) {
            throw await this.getInboundDeliverrError(error, url);
        }
    }
    async getBoxLabelByCdskuPdf(cdsku, labelFormat = BoxLabelsFormat_1.BoxLabelsFormat.THERMAL_4_X_6) {
        const url = `/v1/package/box/label/${cdsku}/pdf`;
        return (await this.getClient(ClientUtils_1.DUMMY_SELLER_ID)).axiosInstance
            .request({
            method: "post",
            url,
            data: {
                labelFormat,
            },
            responseType: "arraybuffer",
        })
            .then((response) => new buffer_1.Blob([response.data], { type: "application/pdf" }))
            .catch(async (error) => {
            throw await this.getInboundDeliverrError(error, url);
        });
    }
    async getBoxLabelByCdskusPdf(cdskus, labelFormat = BoxLabelsFormat_1.BoxLabelsFormat.THERMAL_4_X_6) {
        const url = `/v1/package/box/label/bulk/pdf`;
        return (await this.getClient(ClientUtils_1.DUMMY_SELLER_ID)).axiosInstance
            .request({
            method: "post",
            url,
            data: {
                cdskus,
                labelFormat,
            },
            responseType: "stream",
        })
            .then((response) => response.data)
            .catch(async (error) => {
            throw await this.getInboundDeliverrError(error, url);
        });
    }
    async getBoxLabelByCdskuZpl(cdsku, dpi) {
        const url = `/v1/package/box/label/${cdsku}/zpl`;
        try {
            return await (await this.getClient(ClientUtils_1.DUMMY_SELLER_ID)).post(url, { dpi });
        }
        catch (error) {
            throw await this.getInboundDeliverrError(error, url);
        }
    }
    async getBoxLabelByCdskusZpl(cdskus, dpi, options) {
        const url = `/v1/package/box/label/bulk/zpl`;
        try {
            return await (await this.getClient(ClientUtils_1.DUMMY_SELLER_ID)).post(url, { cdskus, dpi, options });
        }
        catch (error) {
            throw await this.getInboundDeliverrError(error, url);
        }
    }
    async generateZplBoxLabelsForShipment(shipmentId) {
        const url = `/v1/package/box/label/shipment/${shipmentId}/zpl`;
        try {
            return await (await this.getClient(ClientUtils_1.DUMMY_SELLER_ID)).post(url, undefined);
        }
        catch (error) {
            throw await this.getInboundDeliverrError(error, url);
        }
    }
    async replaceFreightPallets(sellerId, shipmentId, palletsSetup) {
        const url = `/v1/freight/pallet/replace/${sellerId}/${shipmentId}`;
        try {
            await (await this.getClient(sellerId)).post(url, palletsSetup);
        }
        catch (error) {
            const convertedValidationError = this.maybeValidationError(error);
            if (convertedValidationError) {
                throw convertedValidationError;
            }
            throw await this.getInboundDeliverrError(error, url);
        }
    }
    async replaceFreightPalletsV2(sellerId, shipmentId, palletSetup, freightStrategy) {
        const url = `/v2/freight/pallet/replace/${sellerId}/${shipmentId}`;
        try {
            await (await this.getClient(sellerId)).post(url, { palletSetup, freightStrategy });
        }
        catch (error) {
            const convertedValidationError = this.maybeValidationError(error);
            if (convertedValidationError) {
                throw convertedValidationError;
            }
            throw await this.getInboundDeliverrError(error, url);
        }
    }
    async getFreightPallets(sellerId, shipmentId) {
        const url = `/v1/freight/pallet/${sellerId}/${shipmentId}`;
        try {
            return (await this.getClient(sellerId)).get(url);
        }
        catch (error) {
            throw await this.getInboundDeliverrError(error, url);
        }
    }
    async replaceFreightPickup(sellerId, shipmentId, pickupData) {
        const url = `/v1/freight/pickup/replace/${sellerId}/${shipmentId}`;
        try {
            await (await this.getClient(sellerId)).post(url, pickupData);
        }
        catch (error) {
            const convertedValidationError = this.maybeValidationError(error);
            if (convertedValidationError) {
                throw convertedValidationError;
            }
            throw await this.getInboundDeliverrError(error, url);
        }
    }
    async getFreightPickup(sellerId, shipmentId) {
        const url = `/v1/freight/pickup/${sellerId}/${shipmentId}`;
        try {
            return (await this.getClient(sellerId)).get(url);
        }
        catch (error) {
            throw await this.getInboundDeliverrError(error, url);
        }
    }
    async getFreightStates(sellerId, shipmentIds) {
        const url = `/v1/freight/current-state/${sellerId}`;
        try {
            return (await this.getClient(sellerId)).get(url, { shipmentIds });
        }
        catch (error) {
            throw await this.getInboundDeliverrError(error, url);
        }
    }
    async initQuotes(sellerId, shipmentId, shippingOption) {
        const url = `/v1/shipping-rate/quote/init/${shippingOption}/${sellerId}/${shipmentId}`;
        try {
            await (await this.getClient(sellerId)).post(url, undefined);
        }
        catch (error) {
            throw await this.getInboundDeliverrError(error, url);
        }
    }
    async getQuoteResults(sellerId, shipmentId, shippingOption) {
        const url = `/v1/shipping-rate/quote/result/${shippingOption}/${sellerId}/${shipmentId}`;
        try {
            return (await this.getClient(sellerId)).get(url, undefined);
        }
        catch (error) {
            throw await this.getInboundDeliverrError(error, url);
        }
    }
    async initRatePurchase(sellerId, shipmentId, shippingOption, shippingRateQuoteIds) {
        const url = `/v1/shipping-rate/buy/init/${shippingOption}/${sellerId}/${shipmentId}`;
        try {
            await (await this.getClient(sellerId)).post(url, {
                shippingRateQuoteIds,
            });
        }
        catch (error) {
            throw await this.getInboundDeliverrError(error, url);
        }
    }
    async getRatePurchaseResults(sellerId, shipmentId, shippingOption) {
        const url = `/v1/shipping-rate/buy/result/${shippingOption}/${sellerId}/${shipmentId}`;
        try {
            return (await this.getClient(sellerId)).get(url, undefined);
        }
        catch (error) {
            throw await this.getInboundDeliverrError(error, url);
        }
    }
    async getEligibleShippingConfig(shipmentId, fromAddress, toAddress) {
        const url = `/v1/shipping-rate/config/${shipmentId}`;
        try {
            return await (await this.getClient(ClientUtils_1.DUMMY_SELLER_ID)).post(url, { fromAddress, toAddress });
        }
        catch (error) {
            throw await this.getInboundDeliverrError(error, url);
        }
    }
    async getSimplifiedFreightQuotes(fromZip, toZip) {
        const url = `/v1/shipping-rate/freight/simplified/from/${fromZip}/to/${toZip}`;
        try {
            return await (await this.getClient(ClientUtils_1.DUMMY_SELLER_ID)).post(url, undefined);
        }
        catch (error) {
            throw await this.getInboundDeliverrError(error, url);
        }
    }
    async spdTransportationTrackingUpdate(summary, details) {
        const url = `/v2/tracking/packages`;
        try {
            await (await this.getClient(ClientUtils_1.DUMMY_SELLER_ID)).post(url, {
                summary,
                details,
            });
        }
        catch (error) {
            throw await this.getInboundDeliverrError(error, url);
        }
    }
    async editShipmentExpectedQuantities(sellerId, shipmentId, updatedQties) {
        const url = `/v1/shipment/${sellerId}/${shipmentId}/update-expected-qty`;
        try {
            return (await this.getClient(sellerId)).put(url, updatedQties);
        }
        catch (error) {
            throw await this.getInboundDeliverrError(error, url);
        }
    }
    async activatePackage(cdsku) {
        const url = `/v1/package/${cdsku}/activate`;
        try {
            return (await this.getClient(ClientUtils_1.DUMMY_SELLER_ID)).put(url, {});
        }
        catch (error) {
            throw await this.getInboundDeliverrError(error, url);
        }
    }
    async findPrepRequestByShippingPlanId(sellerId, shippingPlanId) {
        const url = `/v1/prep/${sellerId}/${shippingPlanId}/request`;
        try {
            return await (await this.getClient(sellerId)).get(url);
        }
        catch (error) {
            throw await this.getInboundDeliverrError(error, url);
        }
    }
    async findPrepRequestByPoNum(poNum) {
        const url = `/v1/prep/request/poNum/${poNum}`;
        try {
            return await (await this.getClient(ClientUtils_1.DUMMY_SELLER_ID)).get(url);
        }
        catch (error) {
            throw await this.getInboundDeliverrError(error, url);
        }
    }
    async findPrepJobBySellerAndSource(sellerId, sourceType, sourceId) {
        const url = `/v1/prep/${sellerId}/source/${sourceType}/${sourceId}`;
        try {
            return await (await this.getClient(sellerId)).get(url);
        }
        catch (error) {
            throw await this.getInboundDeliverrError(error, url);
        }
    }
    async requestBoxContentLabelsPrep({ sellerId, shippingPlanId, }) {
        const url = `/v1/prep/${sellerId}/${shippingPlanId}/box-content-labels`;
        try {
            return await (await this.getClient(sellerId)).put(url, {});
        }
        catch (error) {
            throw await this.getInboundDeliverrError(error, url);
        }
    }
    async removeBoxContentLabelsPrep({ sellerId, shippingPlanId, }) {
        const url = `/v1/prep/${sellerId}/${shippingPlanId}/box-content-labels`;
        try {
            return await (await this.getClient(sellerId)).delete(url);
        }
        catch (error) {
            throw await this.getInboundDeliverrError(error, url);
        }
    }
    async updateSimplePrepForShippingPlan({ sellerId, shippingPlanId, requestedPrepTypes, }) {
        const url = `/v1/prep/simple/${sellerId}/${shippingPlanId}`;
        try {
            return await (await this.getClient(sellerId)).put(url, {
                requestedPrepTypes,
            });
        }
        catch (error) {
            throw await this.getInboundDeliverrError(error, url);
        }
    }
    async updateSelfServePrepForShippingPlan({ sellerId, shippingPlanId, shippingPlanSelfServePrepRequest, }) {
        const url = `/v1/prep/self-serve/${sellerId}/${shippingPlanId}`;
        try {
            return await (await this.getClient(sellerId)).put(url, shippingPlanSelfServePrepRequest);
        }
        catch (error) {
            throw await this.getInboundDeliverrError(error, url);
        }
    }
    async updateSelfServePrepRequestsForShippingPlan({ sellerId, shippingPlanId, shippingPlanSelfServePrepOptionsRequest, }) {
        const url = `/v1/prep/self-serve-requests/${sellerId}/${shippingPlanId}`;
        try {
            return await (await this.getClient(sellerId)).put(url, shippingPlanSelfServePrepOptionsRequest);
        }
        catch (error) {
            throw await this.getInboundDeliverrError(error, url);
        }
    }
    async getSelfServePrepForShippingPlan(sellerId, shippingPlanId) {
        const url = `/v1/prep/self-serve/${sellerId}/${shippingPlanId}`;
        try {
            return await (await this.getClient(sellerId)).get(url);
        }
        catch (error) {
            throw await this.getInboundDeliverrError(error, url);
        }
    }
    async getSimplePrepByShippingPlanId({ sellerId, shippingPlanId, }) {
        const url = `/v1/prep/simple/${sellerId}/${shippingPlanId}`;
        try {
            return await (await this.getClient(sellerId)).get(url);
        }
        catch (error) {
            throw await this.getInboundDeliverrError(error, url);
        }
    }
    async findPrepJobByShippingPlanId(sellerId, shippingPlanId) {
        const url = `/v1/prep/${sellerId}/${shippingPlanId}/job`;
        try {
            return await (await this.getClient(sellerId)).get(url);
        }
        catch (error) {
            throw await this.getInboundDeliverrError(error, url);
        }
    }
    async requestFloorLoadedContainerPrep(sellerId, shippingPlanId, containerDetail) {
        const url = `/v1/prep/${sellerId}/${shippingPlanId}/floor-loaded-container`;
        try {
            return await (await this.getClient(sellerId)).post(url, containerDetail);
        }
        catch (error) {
            throw await this.getInboundDeliverrError(error, url);
        }
    }
    async undoCompletedPrep(sellerId, shippingPlanId) {
        const url = `/v1/prep/${sellerId}/${shippingPlanId}/undo-complete`;
        try {
            return await (await this.getClient(sellerId)).put(url, undefined);
        }
        catch (error) {
            throw await this.getInboundDeliverrError(error, url);
        }
    }
    async generateCompleteTestShipToOneShippingPlan(sellerId, crossdockWarehouseId, items) {
        const url = `/test/shipping-plan/ship-to-one`;
        try {
            return (await this.getClient(sellerId)).post(url, { sellerId, crossdockWarehouseId, items });
        }
        catch (error) {
            throw await this.getInboundDeliverrError(error, url);
        }
    }
    async shippingPlanSpreadsheetUpload(sellerId, spreadsheetFile, casePack, sessionUuid, type) {
        const url = `/v1/shipping-plan/${sellerId}/generation/from-upload`;
        try {
            const formData = new FormData();
            formData.append("uploaded", spreadsheetFile);
            formData.append("casePack", casePack.toString());
            formData.append("originalFileName", spreadsheetFile.name);
            formData.append("sessionUuid", sessionUuid);
            formData.append("type", type !== null && type !== void 0 ? type : ShipmentFileUploadType_1.ShipmentFileUploadType.DEFAULT);
            return (await this.getClient(sellerId)).request({
                url,
                method: "POST",
                data: formData,
            });
        }
        catch (error) {
            throw await this.getInboundDeliverrError(error, url);
        }
    }
    async reserveAsnLabels(sellerId, creationReason, numLabels) {
        const url = `/v1/asn/label/reservation`;
        return (await this.getClient(sellerId)).post(url, {
            sellerId,
            creationReason,
            numLabels,
        });
    }
    async createAsnFromReceivable(sellerId, warehouseId, creationReason, cdskus) {
        const url = `/v1/asn/commit/fromReceivable`;
        return (await this.getClient(sellerId)).post(url, {
            sellerId,
            warehouseId,
            creationReason,
            cdskus,
        });
    }
    async createAsnFromReceivableV2(sellerId, warehouseId, boxLabels, palletLabels, creationReason, asnLabel, skuDetails) {
        const url = `/v2/asn/commit/fromReceivable`;
        return (await this.getClient(sellerId)).post(url, {
            sellerId,
            warehouseId,
            boxLabels,
            palletLabels,
            skuDetails,
            creationReason,
            asnLabel,
        });
    }
    async createAsnFromSkus(sellerId, warehouseId, creationReason, skuQuantities) {
        const url = `/v1/asn/commit/fromSkus`;
        return (await this.getClient(sellerId)).post(url, {
            sellerId,
            warehouseId,
            creationReason,
            skuQuantities,
        });
    }
    async createEmptyAsn(createEmptyAsnRequest = {}) {
        return (await this.getClient(ClientUtils_1.DUMMY_SELLER_ID)).post(`/v1/asn/create-empty-asn`, createEmptyAsnRequest);
    }
    async archiveAsn(asnId) {
        return (await this.getClient(ClientUtils_1.DUMMY_SELLER_ID)).post(`/v1/asn/status/archival`, {
            asnId,
        });
    }
    async addDskusAndSubmitAsn(asnId, warehouseId, skuQuantities) {
        return (await this.getClient(ClientUtils_1.DUMMY_SELLER_ID)).post(`/v1/asn/${asnId}/submit-dskus/${warehouseId}`, {
            skuQuantities,
        });
    }
    async createPackageFromSkus(sellerId, warehouseId, creationReason, skuQuantities) {
        const url = `/v1/package/commit/fromSkus`;
        return (await this.getClient(sellerId)).post(url, {
            sellerId,
            warehouseId,
            creationReason,
            skuQuantities,
        });
    }
    async createPackageFromSkusV2(sellerId, warehouseId, createdBy, creationReason, skuDetails, isHighRisk = false, captureLotExpiry = false) {
        const url = `/v2/package/commit/fromSkus`;
        const packageCreationRequest = {
            sellerId,
            warehouseId,
            createdBy,
            creationReason,
            isHighRisk,
            captureLotExpiry,
        };
        return (await this.getClient(sellerId)).post(url, {
            packageCreationRequest,
            skuDetails,
        });
    }
    async movePackagesToPlan(cdskus, toShippingPlanId) {
        const url = `/admin/v1/package/cross-dock/move-packages-to-plan`;
        try {
            return (await this.getClient(ClientUtils_1.DUMMY_SELLER_ID)).post(url, {
                cdskus,
                toShippingPlanId,
            });
        }
        catch (error) {
            throw await this.getInboundDeliverrError(error, url);
        }
    }
    async getForwardingShipmentsByDskusAndDateRange(shipmentDskuMatchRequest) {
        const url = `/v1/shipment/shipments/crossdock/dskus`;
        try {
            return (await this.getClient(ClientUtils_1.DUMMY_SELLER_ID)).post(url, shipmentDskuMatchRequest);
        }
        catch (error) {
            throw await this.getInboundDeliverrError(error, url);
        }
    }
    async setShippingPlanLotExpiration(sellerId, shippingPlanId, lotExpiration) {
        const url = `/v1/shipping-plan/${sellerId}/${shippingPlanId}/item/lotExpiration`;
        try {
            return (await this.getClient(sellerId)).put(url, lotExpiration);
        }
        catch (error) {
            throw await this.getInboundDeliverrError(error, url);
        }
    }
    async setShippingPlanLotExpirationV2(sellerId, shippingPlanId, lotExpiration) {
        const url = `/v2/shipping-plan/${sellerId}/${shippingPlanId}/item/lotExpiration`;
        try {
            return (await this.getClient(sellerId)).put(url, lotExpiration);
        }
        catch (error) {
            throw await this.getInboundDeliverrError(error, url);
        }
    }
    async shippingPlanHasLotExpiration(sellerId, shippingPlanId) {
        const url = `/v1/shipping-plan/${sellerId}/${shippingPlanId}/item/lotExpiration`;
        try {
            return (await this.getClient(sellerId)).get(url);
        }
        catch (error) {
            throw await this.getInboundDeliverrError(error, url);
        }
    }
    async haveDskusEverBeenShipped(sellerId, dskus) {
        const url = `/v1/shipment/${sellerId}/query/dskus-previously-shipped`;
        try {
            return (await this.getClient(sellerId)).post(url, { dskus });
        }
        catch (error) {
            throw await this.getInboundDeliverrError(error, url);
        }
    }
    async createShopifyForwardingShipment(shippingPlanRequest) {
        const url = `/v1/shopify/forwarding-shipment`;
        try {
            return (await this.getClient(ClientUtils_1.DUMMY_SELLER_ID)).post(url, shippingPlanRequest);
        }
        catch (error) {
            throw await this.getInboundDeliverrError(error, url);
        }
    }
    async getShopifyForwardingShipmentSummary(sellerId, shipmentId) {
        const url = `v1/shopify/${sellerId}/forwarding-shipment-summary/${shipmentId}`;
        try {
            return (await this.getClient(sellerId)).get(url);
        }
        catch (error) {
            throw await this.getInboundDeliverrError(error, url);
        }
    }
    async createShopifyForwardingShipmentV2(shipmentRequest) {
        const url = `/v2/shopify/forwarding-shipment`;
        try {
            return (await this.getClient(ClientUtils_1.DUMMY_SELLER_ID)).post(url, shipmentRequest);
        }
        catch (error) {
            throw await this.getInboundDeliverrError(error, url);
        }
    }
    async createShopifyForwardingShipmentV3(shipmentRequest) {
        const url = `/v3/shopify/forwarding-shipment`;
        try {
            return (await this.getClient(ClientUtils_1.DUMMY_SELLER_ID)).post(url, shipmentRequest);
        }
        catch (error) {
            throw await this.getInboundDeliverrError(error, url);
        }
    }
    async createShopifyForwardingShipmentV4(shipmentRequest) {
        const url = `/v4/shopify/forwarding-shipments`;
        try {
            return (await this.getClient(ClientUtils_1.DUMMY_SELLER_ID)).post(url, shipmentRequest);
        }
        catch (error) {
            throw await this.getInboundDeliverrError(error, url);
        }
    }
    async createShopifyShipmentV1(shippingPlanRequest) {
        const url = `/v1/shopify/shipment`;
        try {
            return (await this.getClient(ClientUtils_1.DUMMY_SELLER_ID)).post(url, shippingPlanRequest);
        }
        catch (error) {
            throw await this.getInboundDeliverrError(error, url);
        }
    }
    async updateShopifyForwardingShipment(shipmentRequest, sellerId, shipmentId) {
        const url = `/v2/shopify/${sellerId}/forwarding-shipment/${shipmentId}`;
        try {
            return (await this.getClient(sellerId)).patch(url, shipmentRequest);
        }
        catch (error) {
            throw await this.getInboundDeliverrError(error, url);
        }
    }
    async updateShopifyForwardingShipmentV3(shipmentRequest, sellerId, shipmentId) {
        const url = `/v3/shopify/${sellerId}/forwarding-shipment/${shipmentId}`;
        try {
            return (await this.getClient(sellerId)).patch(url, shipmentRequest);
        }
        catch (error) {
            throw await this.getInboundDeliverrError(error, url);
        }
    }
    async getShopifyForwardingShipmentSummaryV2(sellerId, shipmentId) {
        const url = `v2/shopify/${sellerId}/forwarding-shipment-summary/${shipmentId}`;
        try {
            return (await this.getClient(sellerId)).get(url);
        }
        catch (error) {
            throw await this.getInboundDeliverrError(error, url);
        }
    }
    async getShopifyForwardingShipmentV3(sellerId, shipmentId) {
        const url = `/v3/shopify/${sellerId}/forwarding-shipments/${shipmentId}`;
        try {
            return (await this.getClient(sellerId)).get(url);
        }
        catch (error) {
            throw await this.getInboundDeliverrError(error, url);
        }
    }
    async getShopifyForwardingShipmentV4(sellerId, shipmentId) {
        const url = `/v4/shopify/${sellerId}/forwarding-shipments/${shipmentId}`;
        try {
            return (await this.getClient(sellerId)).get(url);
        }
        catch (error) {
            throw await this.getInboundDeliverrError(error, url);
        }
    }
    async getShopifyShipmentV1(sellerId, shipmentId) {
        const url = `/v1/shopify/${sellerId}/shipments/${shipmentId}`;
        try {
            return (await this.getClient(sellerId)).get(url);
        }
        catch (error) {
            throw await this.getInboundDeliverrError(error, url);
        }
    }
    async getShopifyShipmentV2(sellerId, shipmentId) {
        const url = `/v2/shopify/${sellerId}/shipments/${shipmentId}`;
        try {
            return (await this.getClient(sellerId)).get(url);
        }
        catch (error) {
            throw await this.getInboundDeliverrError(error, url);
        }
    }
    async getShopifyForwardingShipmentsV3(sellerId, shipmentIds) {
        const params = new URLSearchParams();
        shipmentIds.forEach((id) => params.append("shipmentIds", id));
        const url = `/v3/shopify/${sellerId}/forwarding-shipments?${params.toString()}`;
        try {
            return (await this.getClient(sellerId)).get(url);
        }
        catch (error) {
            throw await this.getInboundDeliverrError(error, url);
        }
    }
    async getShopifyForwardingShipmentsV4(sellerId, shipmentIds, limit, offset, statuses) {
        const params = new URLSearchParams();
        shipmentIds.forEach((id) => params.append("shipmentIds", id));
        params.append("limit", limit.toString());
        params.append("offset", offset.toString());
        if (statuses) {
            statuses.forEach((status) => params.append("statuses", status));
        }
        const url = `/v4/shopify/${sellerId}/forwarding-shipments?${params.toString()}`;
        try {
            return (await this.getClient(sellerId)).get(url);
        }
        catch (error) {
            throw await this.getInboundDeliverrError(error, url);
        }
    }
    async getShopifyShipmentsV1(sellerId, shipmentIds, limit, offset, statuses) {
        const params = new URLSearchParams();
        shipmentIds.forEach((id) => params.append("shipmentIds", id));
        params.append("limit", limit.toString());
        params.append("offset", offset.toString());
        if (statuses) {
            statuses.forEach((status) => params.append("statuses", status));
        }
        const url = `/v1/shopify/${sellerId}/shipments?${params.toString()}`;
        try {
            return (await this.getClient(sellerId)).get(url);
        }
        catch (error) {
            throw await this.getInboundDeliverrError(error, url);
        }
    }
    async getShopifyShipmentsV2(sellerId, shipmentIds, limit, offset, statuses) {
        const params = new URLSearchParams();
        shipmentIds.forEach((id) => params.append("shipmentIds", id));
        params.append("limit", limit.toString());
        params.append("offset", offset.toString());
        if (statuses) {
            statuses.forEach((status) => params.append("statuses", status));
        }
        const url = `/v2/shopify/${sellerId}/shipments?${params.toString()}`;
        try {
            return (await this.getClient(sellerId)).get(url);
        }
        catch (error) {
            throw await this.getInboundDeliverrError(error, url);
        }
    }
    async getShipmentDocumentResult(sellerId, shipmentId, documentOption, documentFormat, language = commons_objects_1.SupportedLanguage.ENGLISH) {
        const url = `v1/shopify/${sellerId}/shipment/${shipmentId}/document?documentOption=${documentOption}&documentFormat=${documentFormat}&language=${language}`;
        try {
            return (await this.getClient(sellerId)).get(url);
        }
        catch (error) {
            throw await this.getInboundDeliverrError(error, url);
        }
    }
    async getShopifyQuoteResult(shipmentId, shippingOption = InboundShipment_1.NetworkInboundShippingOption.SPD) {
        const url = `/v1/shopify/shipping-rate/quote/${shipmentId}?shippingOption=${shippingOption}`;
        try {
            return (await this.getClient(ClientUtils_1.DUMMY_SELLER_ID)).get(url);
        }
        catch (error) {
            throw await this.getInboundDeliverrError(error, url);
        }
    }
    async initShopifyQuoteProcess(shipmentId, shippingOption = InboundShipment_1.NetworkInboundShippingOption.SPD) {
        const url = `/v1/shopify/shipping-rate/quote/${shipmentId}?shippingOption=${shippingOption}`;
        try {
            return (await this.getClient(ClientUtils_1.DUMMY_SELLER_ID)).post(url, {});
        }
        catch (error) {
            throw await this.getInboundDeliverrError(error, url);
        }
    }
    async getShopifyPurchasedQuotes(shipmentId, shippingOption = InboundShipment_1.NetworkInboundShippingOption.SPD) {
        const url = `/v1/shopify/shipping-rate/quote/buy/${shipmentId}?shippingOption=${shippingOption}`;
        try {
            return (await this.getClient(ClientUtils_1.DUMMY_SELLER_ID)).get(url);
        }
        catch (error) {
            throw await this.getInboundDeliverrError(error, url);
        }
    }
    async initShopifyBuyQuoteProcess(shipmentId, quoteIds) {
        const url = `/v1/shopify/shipping-rate/quote/buy/${shipmentId}`;
        try {
            return (await this.getClient(ClientUtils_1.DUMMY_SELLER_ID)).post(url, { quoteIds });
        }
        catch (error) {
            throw await this.getInboundDeliverrError(error, url);
        }
    }
    async createShippingPlanFromExternalRequest(sellerId, shippingPlanRequest) {
        const url = `/v1/external-api/${sellerId}/shipping-plan`;
        try {
            return (await this.getClient(sellerId)).post(url, shippingPlanRequest);
        }
        catch (error) {
            throw await this.getInboundDeliverrError(error, url);
        }
    }
    async getExternalShippingPlanSummary(sellerId, shippingPlanId) {
        const url = `/v1/external-api/${sellerId}/${shippingPlanId}/summary`;
        try {
            return (await this.getClient(sellerId)).get(url);
        }
        catch (error) {
            throw await this.getInboundDeliverrError(error, url);
        }
    }
    async getExternalShippingPlanSummaryByExternalId(sellerId, externalShippingPlanId) {
        const url = `/v1/external-api/${sellerId}/${externalShippingPlanId}/summary-by-external-id`;
        try {
            return (await this.getClient(sellerId)).get(url);
        }
        catch (error) {
            throw await this.getInboundDeliverrError(error, url);
        }
    }
    async getExternalShippingPlanBoxLabels(labelRequest) {
        const { sellerId, shippingPlanId, shipmentId, labelFormat, language } = labelRequest;
        const url = `/v1/external-api/${sellerId}/labels`;
        try {
            return await (await this.getClient(sellerId)).get(url, {
                shippingPlanId,
                shipmentId,
                labelFormat,
                language,
            });
        }
        catch (error) {
            throw await this.getInboundDeliverrError(error, url);
        }
    }
    async updateShippingOption(sellerId, shipmentId, shippingOption) {
        const url = `/v1/shipment/${sellerId}/${shipmentId}/shipping-option`;
        try {
            return await (await this.getClient(sellerId)).put(url, {
                shippingOption,
            });
        }
        catch (error) {
            throw await this.getInboundDeliverrError(error, url);
        }
    }
    async asnReceiveValidateAsn(warehouseId, asnLabel, requestBatchId) {
        const url = `/v1/receiving/asn/validation/asn/${warehouseId}`;
        return (await this.getClient(ClientUtils_1.DUMMY_SELLER_ID)).post(url, {
            asnLabel,
            requestBatchId,
        });
    }
    async asnReceiveValidateBarcode(warehouseId, asnLabel, barcode, requestBatchId) {
        const url = `/v1/receiving/asn/validation/sku/${warehouseId}`;
        return (await this.getClient(ClientUtils_1.DUMMY_SELLER_ID)).post(url, {
            asnLabel,
            barcode,
            requestBatchId,
        });
    }
    async asnReceiveValidateBarcodeWithRestrictions(warehouseId, asnLabel, barcode, requestBatchId) {
        const url = `/v3/receiving/asn/validation/sku/${warehouseId}`;
        return (await this.getClient(ClientUtils_1.DUMMY_SELLER_ID)).post(url, {
            asnLabel,
            barcode,
            requestBatchId,
        });
    }
    async asnReceiveValidateBarcodeV2(warehouseId, asnLabel, barcode, requestBatchId) {
        const url = `/v2/receiving/asn/validation/sku/${warehouseId}`;
        return (await this.getClient(ClientUtils_1.DUMMY_SELLER_ID)).post(url, {
            asnLabel,
            barcode,
            requestBatchId,
        });
    }
    async asnReceiveAndCommit(warehouseId, dedupKey, asnLabel, dsku, receivedDelta, location, requestBatchId) {
        const url = `/v1/receiving/asn/receipt/request/${warehouseId}`;
        return (await this.getClient(ClientUtils_1.DUMMY_SELLER_ID)).post(url, {
            warehouseId,
            dedupKey,
            asnLabel,
            dsku,
            receivedDelta,
            location,
            requestBatchId,
        });
    }
    async markAsnReceivingCompleted(asnLabel, warehouseId) {
        const url = `/v1/receiving/asn/completion/${warehouseId}`;
        return (await this.getClient(ClientUtils_1.DUMMY_SELLER_ID)).post(url, {
            asnLabel,
        });
    }
    async asnReceiveAndCommitMultiLocation(warehouseId, dedupKey, asnLabel, dsku, receivesByLocation, requestBatchId, expirationDate, lotNumber) {
        const url = `/v2/receiving/asn/receipt/request/${warehouseId}`;
        return (await this.getClient(ClientUtils_1.DUMMY_SELLER_ID)).post(url, {
            warehouseId,
            dedupKey,
            asnLabel,
            dsku,
            receivesByLocation,
            requestBatchId,
            expirationDate,
            lotNumber,
        });
    }
    async asnReceiveAndCommitMultiLocationV3(warehouseId, dedupKey, asnLabel, dsku, receivesByLocation, requestBatchId, expirationDate, lotNumber, images) {
        const url = `/v3/receiving/asn/receipt/request/${warehouseId}`;
        return (await this.getClient(ClientUtils_1.DUMMY_SELLER_ID)).post(url, {
            warehouseId,
            dedupKey,
            asnLabel,
            dsku,
            receivesByLocation,
            requestBatchId,
            expirationDate,
            lotNumber,
            images,
        });
    }
    async getHubScannedPalletCountByTrackingCode(trackingCode) {
        const url = `/v1/pallet/hub-scanned/${trackingCode}`;
        try {
            return await (await this.getClient(ClientUtils_1.DUMMY_SELLER_ID)).get(url);
        }
        catch (err) {
            throw await this.getInboundDeliverrError(err, url);
        }
    }
    async hasInactiveShipmentsWithLabels(sellerId, shippingPlanId) {
        const url = `/v1/shipping-plan/${sellerId}/${shippingPlanId}/has-inactive-shipments-with-labels`;
        try {
            return await (await this.getClient(sellerId)).get(url);
        }
        catch (error) {
            throw await this.getInboundDeliverrError(error, url);
        }
    }
    async getSellerInboundUnits(sellerId, dsku, inboundSources) {
        const url = `/v1/inbound-units-cache/${sellerId}/${dsku}`;
        try {
            return inboundSources
                ? await (await this.getClient(sellerId)).get(url, { inboundSources })
                : await (await this.getClient(sellerId)).get(url);
        }
        catch (error) {
            throw await this.getInboundDeliverrError(error, url);
        }
    }
    async reservePackages(warehouseId, numPackages, packageCreator) {
        const url = `/v1/package/reserve`;
        return (await this.getClient(ClientUtils_1.DUMMY_SELLER_ID)).post(url, {
            warehouseId,
            numPackages,
            packageCreator,
        });
    }
    async copyPackageData(sellerCdsku, proxyCdsku, packageCreator) {
        const url = `/v1/package/reserve/copy-package-data`;
        return (await this.getClient(ClientUtils_1.DUMMY_SELLER_ID)).post(url, {
            sellerCdsku,
            proxyCdsku,
            packageCreator,
        });
    }
    async createBulkPallet(palletLabel, dsku, numUnitsPerBox, numIdenticalBoxes, expirationDate, lotNumber, creator, creationReason) {
        const url = `/v1/pallet/commit/bulk`;
        return (await this.getClient(ClientUtils_1.DUMMY_SELLER_ID)).post(url, {
            palletLabel,
            dsku,
            numUnitsPerBox,
            numIdenticalBoxes,
            expirationDate,
            lotNumber,
            creator,
            creationReason,
        });
    }
    async createWarehouseProducts(warehouseId, dskus, forceCreateAllProducts) {
        const url = `/v1/asn/products/commit/byWarehouseSku`;
        try {
            return (await this.getClient(ClientUtils_1.DUMMY_SELLER_ID)).post(url, {
                warehouseId,
                forceCreateAllProducts,
                dskus,
            });
        }
        catch (error) {
            throw await this.getInboundDeliverrError(error, url);
        }
    }
    async estimateUnitsInTransit(dskus) {
        const url = `/v1/pallet/estimate-units-in-transit`;
        return (await this.getClient(ClientUtils_1.DUMMY_SELLER_ID)).post(url, {
            dskus,
        });
    }
    async switchCrossdock(asnId, crossdockId) {
        const url = `/v1/asn/${asnId}/switch-crossdock/${crossdockId}`;
        return (await this.getClient(ClientUtils_1.DUMMY_SELLER_ID)).post(url, {});
    }
    async generateSingleDirectShipment(shippingPlanId, warehouseId) {
        const url = `/v1/shipment/single-direct/create`;
        return (await this.getClient(ClientUtils_1.DUMMY_SELLER_ID)).post(url, {
            shippingPlanId,
            warehouseId,
        });
    }
    async getNetworkProduct(request) {
        const url = `/v1/3pl-partner/product/${request.requestedBarcode}`;
        return (await this.getClient(ClientUtils_1.DUMMY_SELLER_ID)).get(url);
    }
    async getExternalFreightPalletLabels(shipmentId, sellerId, options) {
        const url = `/v1/freight/pallet/${sellerId}/${shipmentId}/external-pallet-labels`;
        const { data } = await (await this.getClient(sellerId))
            .request({
            method: "GET",
            url,
            params: options,
            responseType: "blob",
            headers: { responseEncoding: "binary" },
        })
            .catch(async (error) => {
            throw await this.getInboundDeliverrError(error, url);
        });
        return data;
    }
    async previewAttribution({ warehouseEventId, }) {
        const url = `/v1/receive-attribution/preview`;
        return (await this.getClient(ClientUtils_1.DUMMY_SELLER_ID)).post(url, {
            warehouseEventId,
        });
    }
    async persistAttribution({ warehouseEventId, }) {
        const url = `/v1/receive-attribution/commit`;
        return (await this.getClient(ClientUtils_1.DUMMY_SELLER_ID)).post(url, {
            warehouseEventId,
        });
    }
    async previewBoxAssignmentAttribution(body) {
        const url = `/v1/receiving/attribution/box-assignment/preview`;
        return (await this.getClient(ClientUtils_1.DUMMY_SELLER_ID)).post(url, body);
    }
    async persistBoxAssignmentAttribution(body) {
        const url = `/v1/receiving/attribution/box-assignment/commit`;
        return (await this.getClient(ClientUtils_1.DUMMY_SELLER_ID)).post(url, body);
    }
    async updateShippingPlanTask(shippingPlanId, sellerId, taskId, taskUpdate) {
        const url = `/v1/shipping-plan/${sellerId}/${shippingPlanId}/task/${taskId}`;
        try {
            return await (await this.getClient(sellerId)).patch(url, taskUpdate);
        }
        catch (error) {
            throw this.getInboundDeliverrError(error, url);
        }
    }
    async getShippingPlanTasks(shippingPlanId, sellerId) {
        const url = `/v1/shipping-plan/${sellerId}/${shippingPlanId}/tasks`;
        try {
            return await (await this.getClient(sellerId)).get(url);
        }
        catch (error) {
            throw this.getInboundDeliverrError(error, url);
        }
    }
    async getIncompleteShippingPlanTasksForSeller() {
        const url = `/v1/shipping-plan/tasks`;
        try {
            return await (await this.getClient(ClientUtils_1.DUMMY_SELLER_ID)).get(url);
        }
        catch (error) {
            throw this.getInboundDeliverrError(error, url);
        }
    }
    async createBookingV2(sellerId, createBookingRequest) {
        const url = `/v2/bookings/${sellerId}`;
        try {
            return await (await this.getClient(sellerId)).post(url, createBookingRequest);
        }
        catch (error) {
            throw this.getInboundDeliverrError(error, url);
        }
    }
    async bookingSetOrigin(sellerId, shippingPlanId, createBookingRequest) {
        const url = `/v2/bookings/${sellerId}/${shippingPlanId}/origin`;
        try {
            return await (await this.getClient(sellerId)).post(url, createBookingRequest);
        }
        catch (error) {
            throw this.getInboundDeliverrError(error, url);
        }
    }
    async bookingAssignSupplier(sellerId, shippingPlanId, supplier) {
        const url = `/v2/bookings/${sellerId}/${shippingPlanId}/supplier`;
        try {
            return await (await this.getClient(sellerId)).post(url, supplier);
        }
        catch (error) {
            throw this.getInboundDeliverrError(error, url);
        }
    }
    async bookingSetDestination(sellerId, shippingPlanId, bookingDestinationRequest) {
        const url = `/v2/bookings/${sellerId}/${shippingPlanId}/destination`;
        try {
            return await (await this.getClient(sellerId)).post(url, bookingDestinationRequest);
        }
        catch (error) {
            throw this.getInboundDeliverrError(error, url);
        }
    }
    async bookingUpdateItem(sellerId, shippingPlanId, bookingItemRequest) {
        const url = `/v2/bookings/${sellerId}/${shippingPlanId}/item`;
        try {
            return await (await this.getClient(sellerId)).post(url, bookingItemRequest);
        }
        catch (error) {
            throw this.getInboundDeliverrError(error, url);
        }
    }
    async bookingSaveFreight(sellerId, shippingPlanId, submitBookingRequest) {
        const url = `/v2/bookings/${sellerId}/${shippingPlanId}/freight`;
        try {
            return await (await this.getClient(sellerId)).post(url, submitBookingRequest);
        }
        catch (error) {
            throw this.getInboundDeliverrError(error, url);
        }
    }
    async submitBooking(sellerId, shippingPlanId, submitBookingRequest) {
        const url = `/v2/bookings/${sellerId}/${shippingPlanId}/submit`;
        try {
            return await (await this.getClient(sellerId)).post(url, submitBookingRequest);
        }
        catch (error) {
            throw this.getInboundDeliverrError(error, url);
        }
    }
    async createBooking(bookingRequest) {
        const url = `/v1/bookings`;
        try {
            return await (await this.getClient(ClientUtils_1.DUMMY_SELLER_ID)).post(url, bookingRequest);
        }
        catch (error) {
            throw this.getInboundDeliverrError(error, url);
        }
    }
    async updateBooking(shippingPlanId, update) {
        const url = `/v1/bookings/shipping-plans/${shippingPlanId}`;
        try {
            return await (await this.getClient(ClientUtils_1.DUMMY_SELLER_ID)).patch(url, update);
        }
        catch (error) {
            throw this.getInboundDeliverrError(error, url);
        }
    }
    async bookingOnboardingCompleted(update) {
        const url = "/v1/bookings/onboarding-completed";
        try {
            return await (await this.getClient(ClientUtils_1.DUMMY_SELLER_ID)).patch(url, update);
        }
        catch (error) {
            throw this.getInboundDeliverrError(error, url);
        }
    }
    async getBooking(shippingPlanId) {
        const url = `/v1/booking/shipping-plan/${shippingPlanId}`;
        try {
            return await (await this.getClient(ClientUtils_1.DUMMY_SELLER_ID)).get(url);
        }
        catch (error) {
            throw this.getInboundDeliverrError(error, url);
        }
    }
    async getBookingQuote(request) {
        const url = "/v1/booking/quotes";
        try {
            return await (await this.getClient(ClientUtils_1.DUMMY_SELLER_ID)).post(url, request);
        }
        catch (error) {
            throw this.getInboundDeliverrError(error, url);
        }
    }
    async getBookingPlaceOptions(request) {
        const url = "/v1/bookings/booking-place-options";
        try {
            return await (await this.getClient(ClientUtils_1.DUMMY_SELLER_ID)).get(url, request);
        }
        catch (error) {
            throw this.getInboundDeliverrError(error, url);
        }
    }
    async getRankedStorageWarehousesForInbounds(sellerId, request) {
        const url = `/v1/bookings/storage/warehouses/inbounds/${sellerId}/withNearestWarehouse`;
        try {
            return await (await this.getClient(sellerId)).post(url, request);
        }
        catch (error) {
            throw this.getInboundDeliverrError(error, url);
        }
    }
    async createDraft(newDraft) {
        const url = "/v1/shipping-plan/draft";
        try {
            await (await this.getClient(ClientUtils_1.DUMMY_SELLER_ID)).post(url, newDraft);
        }
        catch (error) {
            throw this.getInboundDeliverrError(error, url);
        }
    }
    async updateDraft(update) {
        const url = "/v1/shipping-plan/draft";
        try {
            await (await this.getClient(ClientUtils_1.DUMMY_SELLER_ID)).patch(url, update);
        }
        catch (error) {
            throw this.getInboundDeliverrError(error, url);
        }
    }
    async getDraft(shippingPlanId) {
        const url = `/v1/shipping-plan/draft/${shippingPlanId}`;
        try {
            return await (await this.getClient(ClientUtils_1.DUMMY_SELLER_ID)).get(url);
        }
        catch (error) {
            throw this.getInboundDeliverrError(error, url);
        }
    }
    async getDashboardUrl(dashboard, warehouseId) {
        const url = `/v1/reporting/dashboard-url`;
        return (await this.getClient(ClientUtils_1.DUMMY_SELLER_ID)).post(url, {
            dashboard,
            warehouseId,
        });
    }
    async updateAsnPackageLink(body) {
        const url = "/v1/receiving/asn-package/link/commit";
        return (await this.getClient(ClientUtils_1.DUMMY_SELLER_ID)).post(url, body);
    }
    async assignNonAsnReceiveEventToAsn(params) {
        const url = "/v1/receiving/non-receive-event/assignment";
        return (await this.getClient(ClientUtils_1.DUMMY_SELLER_ID)).post(url, params);
    }
    async splitAsnReceiveEvent(params) {
        const url = "/v1/receiving/asn-receive-event/split";
        return (await this.getClient(ClientUtils_1.DUMMY_SELLER_ID)).post(url, params);
    }
    async previewAsnSplitAttribution(params) {
        const url = "/v1/receiving/attribution/asn-receive-event/split/preview";
        return (await this.getClient(ClientUtils_1.EBS_SELLER_ID)).post(url, params);
    }
    async persistAsnSplitAttribution(params) {
        const url = "/v1/receiving/attribution/asn-receive-event/split/commit";
        return (await this.getClient(ClientUtils_1.EBS_SELLER_ID)).post(url, params);
    }
    async previewNonAsnReceiveEventAttribution(params) {
        const url = "/v1/receiving/attribution/non-receive-event/preview";
        return (await this.getClient(ClientUtils_1.DUMMY_SELLER_ID)).post(url, params);
    }
    async persistNonAsnReceiveEventAttribution(params) {
        const url = "/v1/receiving/attribution/non-receive-event/commit";
        return (await this.getClient(ClientUtils_1.DUMMY_SELLER_ID)).post(url, params);
    }
    async previewNonAsnReceiveEventAttributionV2(params) {
        const url = "/v2/receiving/attribution/non-receive-event/preview";
        return (await this.getClient(ClientUtils_1.DUMMY_SELLER_ID)).post(url, params);
    }
    async persistNonAsnReceiveEventAttributionV2(params) {
        const url = "/v2/receiving/attribution/non-receive-event/commit";
        return (await this.getClient(ClientUtils_1.DUMMY_SELLER_ID)).post(url, params);
    }
    async previewAsnPackageLinkAttribution(body) {
        const url = "/v1/receiving/attribution/asn-package-link/preview";
        return (await this.getClient(ClientUtils_1.DUMMY_SELLER_ID)).post(url, body);
    }
    async persistAsnPackageLinkAttribution(body) {
        const url = "/v1/receiving/attribution/asn-package-link/commit";
        return (await this.getClient(ClientUtils_1.DUMMY_SELLER_ID)).post(url, body);
    }
    async getZoneByProductBarcode(body) {
        const url = "/v1/receiving/facility-zone/zone/query/byProductBarcode";
        return (await this.getClient(ClientUtils_1.DUMMY_SELLER_ID)).post(url, body);
    }
    async markShipmentReceivingCompleted(body) {
        const url = `/v1/receiving/shipment/completion`;
        return (await this.getClient(ClientUtils_1.DUMMY_SELLER_ID)).post(url, body);
    }
    async createReceivablePackage(body) {
        const url = `/v1/receiving/box/receivable/creation`;
        return (await this.getClient(ClientUtils_1.DUMMY_SELLER_ID)).post(url, body);
    }
    async assignShipmentToUnlabeledBoxPreview(body) {
        const url = `/v1/receiving/attribution/unlabeled-box-shipment-assignment/preview`;
        return (await this.getClient(ClientUtils_1.DUMMY_SELLER_ID)).post(url, body);
    }
    async assignShipmentToUnlabeledBoxCommit(body) {
        const url = `/v1/receiving/attribution/unlabeled-box-shipment-assignment/commit`;
        return (await this.getClient(ClientUtils_1.DUMMY_SELLER_ID)).post(url, body);
    }
    async getUnlabeledContainersAwaitingAttribution() {
        const url = `/v1/support-portal/unlabeled-container-attribution/query`;
        return (await this.getClient(ClientUtils_1.DUMMY_SELLER_ID)).post(url, {});
    }
    async requestForAppointment(shipmentId, carrierEmailAddress) {
        const url = "/v1/shipment/request-appointment/";
        const requestBody = {
            shipmentId,
            carrierEmailAddress,
        };
        try {
            return await (await this.getClient(ClientUtils_1.DUMMY_SELLER_ID)).post(url, requestBody);
        }
        catch (error) {
            throw await this.getInboundDeliverrError(error, url);
        }
    }
    async getCapableWarehouses(sellerId, dskus) {
        const url = "/v1/capable-warehouses";
        try {
            return await (await this.getClient(sellerId)).post(url, { sellerId, dskus });
        }
        catch (error) {
            throw this.getInboundDeliverrError(error, url);
        }
    }
    async updateShipment(sellerId, shipmentId, shipment) {
        const url = `/v1/shipment/${sellerId}/${shipmentId}`;
        return (await this.getClient(sellerId))
            .patch(url, shipment)
            .catch((error) => {
            throw this.getInboundDeliverrError(error, url);
        });
    }
    async getInternationalWarehousesForClientApp(request) {
        const url = `/v3/booking/warehouses`;
        try {
            return (await this.getClient()).post(url, request);
        }
        catch (error) {
            throw this.getInboundDeliverrError(error, url);
        }
    }
    async createFulfilmentBooking(request) {
        const url = `/v3/booking`;
        try {
            return (await this.getClient()).post(url, request);
        }
        catch (error) {
            throw this.getInboundDeliverrError(error, url);
        }
    }
    isAxiosNetworkError(error) {
        return error.message.toLowerCase() === "network error" || (0, lodash_1.get)(error, "response.status") === 504;
    }
    getAxiosNetworkErrorPayload(error) {
        return !error.response ? error : (0, lodash_1.pick)(error, ["status, headers, body"]);
    }
    async getDeliverrNetworkError(error, url) {
        if (!this.isAxiosNetworkError(error)) {
            return;
        }
        try {
            await (await this.getClient(ClientUtils_1.DUMMY_SELLER_ID)).request({
                url,
                method: "OPTIONS",
            });
            return new errors_1.DeliverrError({
                code: errors_1.ApiError.NETWORK_ERROR,
                payload: this.getAxiosNetworkErrorPayload(error),
            });
        }
        catch (optionsError) {
            return new errors_1.DeliverrError({
                code: errors_1.ApiError.CORS_ERROR,
                payload: this.getAxiosNetworkErrorPayload(optionsError),
            });
        }
    }
    maybeValidationError(err) {
        const deliverrErrorCode = (0, lodash_1.get)(err, "response.data.error.code");
        if (deliverrErrorCode && deliverrErrorCode === "ValidationError") {
            return new errors_1.DeliverrError({
                code: deliverrErrorCode,
                subcode: (0, lodash_1.get)(err, "response.data.details[0].context.key"),
                message: (0, lodash_1.get)(err, "response.data.error.message"),
                payload: (0, lodash_1.get)(err, "response.data.error.payload"),
            });
        }
        return undefined;
    }
    async getInboundDeliverrError(error, url) {
        return ((await this.getClient(ClientUtils_1.DUMMY_SELLER_ID)).maybeGetDeliverrError(error) ||
            (await this.getClient(ClientUtils_1.DUMMY_SELLER_ID)).maybeGetNetworkError(error) ||
            (await this.getDeliverrNetworkError(error, url)) ||
            (await this.getClient(ClientUtils_1.DUMMY_SELLER_ID)).getUnknownError(error));
    }
    withAccessToken(accessToken) {
        this.dynamicClient.lambdaApiClient.withAccessToken(accessToken);
        this.dynamicClient.ebsApiClient.withAccessToken(accessToken);
    }
    addBeforeEach(beforeEach) {
        this.dynamicClient.lambdaApiClient.addBeforeEach(beforeEach);
        this.dynamicClient.ebsApiClient.addBeforeEach(beforeEach);
    }
}
exports.InboundClient = InboundClient;
exports.default = InboundClient;
//# sourceMappingURL=InboundClient.js.map